const short_uuid = require("short-uuid");
const translator = short_uuid();

import ExcelJS from "exceljs";
import MONTHS from "@/config/MONTHS";
import { PRODUCT_TYPES } from "@/config/TYPES";
import { ExportService } from "@/modules/exports";
import ProductService from "@/modules/products/service";
import moneyService from "@/services/money";

/**
 * Expense products are never shown in the program itinerary.
 * @template T
 * @param {T & { productType?: string, isShowInProgram?: boolean }} item
 * @returns {T & { productType?: string, isShowInProgram?: boolean }}
 */
function applyShowInProgramForProductType(item) {
  if (item.productType === PRODUCT_TYPES.EXPENSE) {
    item.isShowInProgram = false;
  }
  return item;
}

/**
 * @param {number | string | null | undefined} number
 * @returns {number}
 */
function roundToTwoDecimals(number) {
  if (number == null || Number.isNaN(Number(number))) return 0;
  return Number(Number(number).toFixed(2));
}

// Generate uuid of variable cost or fixed cost
function generateUUID() {
  return translator.generate();
}

/**
 * @param {*} value
 * @param {*} [fallback]
 */
function parseStoredJson(value, fallback = null) {
  if (value == null || value === "") return fallback;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return fallback;
  }
}

function parsePeopleRanges(/** @type {*} */ peopleRanges) {
  const parsed = parseStoredJson(peopleRanges, []);
  return Array.isArray(parsed) ? parsed : [];
}

/**
 * Same allocation as Total Costs: room rates are per person (stored room price / 2),
 * linked suppliers are added to the group (doubled for rooms), then FOCs are removed
 * before dividing by paying clients. Unit price already includes tax, extras, resort fee, and porterage.
 * @returns {{ groupTotal: number, focQuantity: number }}
 */
function getVariableCostAllocation(
  /** @type {*} */ unitPrice,
  /** @type {*} */ numberOfClients,
  /** @type {*} */ matchedSupplierCount,
  /** @type {*} */ productType,
  /** @type {*} */ foc
) {
  const isRoom = productType === "ROOM";
  const numberOfSuppliers = Number(matchedSupplierCount) * (isRoom ? 2 : 1);
  const totalNumber = Number(numberOfClients) + numberOfSuppliers;

  let focBase = foc?.per ?? 1;
  let focTop = foc?.value ?? 0;
  if (focTop === "no") focTop = 0;

  const effectiveFocBase = focBase * (isRoom ? 2 : 1);
  let focQuantity =
    Math.floor((totalNumber - Math.floor(totalNumber / effectiveFocBase)) / effectiveFocBase) * focTop;
  if (isRoom) focQuantity *= 2;

  return {
    groupTotal: (Number(unitPrice) || 0) * (totalNumber - focQuantity),
    focQuantity,
  };
}

function getVariableCostPerPerson(
  /** @type {*} */ unitPrice,
  /** @type {*} */ numberOfClients,
  /** @type {*} */ matchedSupplierCount,
  /** @type {*} */ productType,
  /** @type {*} */ foc
) {
  const clients = Number(numberOfClients);
  if (!clients) return 0;
  return getVariableCostAllocation(unitPrice, clients, matchedSupplierCount, productType, foc).groupTotal / clients;
}

function countMatchedLogisticsSuppliers(/** @type {*} */ logisticsCostUuids, /** @type {*} */ logisticsUuids) {
  const linked = parseStoredJson(logisticsCostUuids, []) || [];
  if (!Array.isArray(linked)) return 0;
  return linked.filter((/** @type {*} */ uuid) => logisticsUuids.includes(uuid)).length;
}

function getTotalCosts(/** @type {*} */ costs, /** @type {*} */ logisticsCosts, /** @type {*} */ peopleRanges) {
  /** @type {number[]} */
  const totalCostByPax = [];
  peopleRanges.forEach((/** @type {*} */ peopleRange) => {
    const numberOfClients = Number(peopleRange.min);
    if (numberOfClients == 0) {
      totalCostByPax.push(0);
      return;
    }

    let totalVariableCosts = 0;
    let totalFixedCosts = 0;

    // Get uuids of all logistics costs related to tours and transportation
    const logisticsUUIDs = logisticsCosts.map((/** @type {*} */ cost) => cost.uuid);

    // Calculate total variable costs
    costs.forEach((/** @type {*} */ cost) => {
      const variableCosts = cost.items.filter((/** @type {*} */ item) => ["PP"].includes(item.costType));
      const fixedCosts = cost.items.filter((/** @type {*} */ item) => ["PG", "PD"].includes(item.costType));

      variableCosts.forEach((/** @type {*} */ item) => {
        // Keep only fixed cost uuids related to tours and transportation
        const matchedSupplierCount = countMatchedLogisticsSuppliers(item.logisticsCostUUIDs, logisticsUUIDs);
        totalVariableCosts += getVariableCostAllocation(
          item.productCost.value.default,
          numberOfClients,
          matchedSupplierCount,
          item.productType,
          item.supplier?.foc
        ).groupTotal;
      });

      fixedCosts.forEach((/** @type {*} */ item) => {
        totalFixedCosts += Number(item.productCost.value.default);
      });
    });

    // Calculate total fixed costs
    logisticsCosts.forEach((/** @type {*} */ cost) => {
      const numberOfDays = (cost.days || []).length;
      const total = cost.productCost.value.default * numberOfDays;
      totalFixedCosts += total;
    });

    // Calculate total cost per pax
    totalCostByPax.push((totalVariableCosts + totalFixedCosts) / numberOfClients);
  });

  return totalCostByPax;
}

/** @type {Record<string, string>} */
const PRODUCT_TYPE_LABELS = {
  ROOM: "Accommodations",
  MEAL: "Meals",
  EXCURSION: "Excursions",
  TOUR: "Tour Guide Services",
  TOUR_LEADER: "Tour Leader",
  TRANS: "Transportation",
  EXPENSE: "Expense",
};

const PRODUCT_TYPE_ORDER = ["ROOM", "MEAL", "EXCURSION", "TOUR", "TOUR_LEADER", "TRANS", "EXPENSE"];

function getHighestPeopleRangeIndex(/** @type {*} */ peopleRanges) {
  if (!peopleRanges?.length) return -1;

  return peopleRanges.reduce((/** @type {*} */ maxIndex, /** @type {*} */ current, /** @type {*} */ index, /** @type {*} */ array) => {
    return Number(current.min) > Number(array[maxIndex]?.min || 0) ? index : maxIndex;
  }, 0);
}

function getCostBreakdownByProductType(/** @type {*} */ costs, /** @type {*} */ logisticsCosts, /** @type {*} */ pax) {
  const numberOfClients = Number(pax);
  /** @type {Record<string, number>} */
  const breakdown = {};

  if (!numberOfClients) {
    return { rows: [], total: 0, pax: numberOfClients };
  }

  const logisticsUUIDs = logisticsCosts.map((/** @type {*} */ cost) => cost.uuid);
  let total = 0;

  const addToBreakdown = (/** @type {string} */ productType, /** @type {number} */ amount) => {
    const key = productType || "OTHER";
    breakdown[key] = (breakdown[key] || 0) + amount;
    total += amount;
  };

  costs.forEach((/** @type {*} */ cost) => {
    const variableCosts = cost.items.filter((/** @type {*} */ item) => ["PP"].includes(item.costType));
    const fixedCosts = cost.items.filter((/** @type {*} */ item) => ["PG", "PD"].includes(item.costType));

    variableCosts.forEach((/** @type {*} */ item) => {
      const matchedSupplierCount = countMatchedLogisticsSuppliers(item.logisticsCostUUIDs, logisticsUUIDs);
      addToBreakdown(
        item.productType,
        getVariableCostAllocation(
          item.productCost.value.default,
          numberOfClients,
          matchedSupplierCount,
          item.productType,
          item.supplier?.foc
        ).groupTotal
      );
    });

    fixedCosts.forEach((/** @type {*} */ item) => {
      addToBreakdown(item.productType, Number(item.productCost.value.default) || 0);
    });
  });

  logisticsCosts.forEach((/** @type {*} */ cost) => {
    const numberOfDays = (cost.days || []).length;
    const itemTotal = (Number(cost.productCost.value.default) || 0) * numberOfDays;
    addToBreakdown(cost.productType, itemTotal);
  });

  const rows = Object.keys(breakdown)
    .sort((a, b) => {
      const orderA = PRODUCT_TYPE_ORDER.indexOf(a);
      const orderB = PRODUCT_TYPE_ORDER.indexOf(b);
      const normalizedA = orderA === -1 ? PRODUCT_TYPE_ORDER.length : orderA;
      const normalizedB = orderB === -1 ? PRODUCT_TYPE_ORDER.length : orderB;
      return normalizedA - normalizedB;
    })
    .map((productType) => ({
      productType,
      label: PRODUCT_TYPE_LABELS[productType] || productType,
      amount: breakdown[productType],
      percent: total ? (breakdown[productType] / total) * 100 : 0,
    }));

  return { rows, total, pax: numberOfClients };
}

/** Max additional periods besides the primary (total periods = 1 + this). */
const MAX_COMPARISON_PERIODS = 4;

function parseComparisonPeriods(/** @type {*} */ comparisonPeriods) {
  if (!comparisonPeriods) return [];
  if (Array.isArray(comparisonPeriods)) return comparisonPeriods.slice(0, MAX_COMPARISON_PERIODS);

  try {
    const parsedComparisonPeriods = JSON.parse(comparisonPeriods);
    return Array.isArray(parsedComparisonPeriods)
      ? parsedComparisonPeriods.slice(0, MAX_COMPARISON_PERIODS)
      : [];
  } catch (_) {
    return [];
  }
}

function mapApiDateToPeriod(/** @type {*} */ startDate, /** @type {*} */ endDate) {
  return {
    startYear: startDate?.year ?? null,
    startMonth: startDate?.month ?? null,
    startDay: startDate?.day ?? null,
    endYear: endDate?.year ?? null,
    endMonth: endDate?.month ?? null,
    endDay: endDate?.day ?? null,
  };
}

function getComparisonPeriodValue(/** @type {*} */ comparisonPeriod) {
  if (comparisonPeriod?.period) return comparisonPeriod.period;
  return mapApiDateToPeriod(comparisonPeriod?.start_date, comparisonPeriod?.end_date);
}

function formatPeriodStartDate(/** @type {*} */ period) {
  const startMonth = period.startMonth != null ? period.startMonth - 1 : 0;
  const startDay = period.startDay != null ? period.startDay : 1;
  const date = new Date(period.startYear, startMonth, startDay);
  const month = date.toLocaleDateString("fr-FR", { month: "short" });
  return period.startDay ? `${period.startDay} ${month}` : month;
}

function formatPeriodStartMonths(/** @type {*} */ periods) {
  const years = periods.map((/** @type {*} */ period) => period.startYear).filter(Boolean);
  const hasDifferentYears = new Set(years).size > 1;

  if (hasDifferentYears) {
    return periods
      .map((/** @type {*} */ period) => {
        const startDate = formatPeriodStartDate(period);
        return period.startYear ? `${startDate} ${period.startYear}` : startDate;
      })
      .join(" / ");
  }

  const months = periods.map(formatPeriodStartDate);
  const year = years[years.length - 1];
  return `${months.join(" / ")}${year ? ` ${year}` : ""}`;
}

function getPeriodDisplayName(/** @type {*} */ periodName, /** @type {*} */ period) {
  return periodName == null ? formatPeriodStartMonths([period]) : periodName;
}

function normalizeCity(/** @type {*} */ value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") return value.value || value.label || "";
  return String(value).trim();
}

function formatDayCities(/** @type {*} */ cityStart, /** @type {*} */ cityEnd, separator = " • ") {
  const fromCity = normalizeCity(cityStart);
  const toCity = normalizeCity(cityEnd);

  if (!fromCity && !toCity) return "";
  if (!fromCity) return toCity;
  if (!toCity) return fromCity;
  if (fromCity.toLowerCase() === toCity.toLowerCase()) return fromCity;

  return `${fromCity}${separator}${toCity}`;
}

function findQuoteRoute(/** @type {*} */ routes, /** @type {*} */ routeId) {
  if (!routeId) return null;
  return (routes || []).find((/** @type {*} */ route) => route && route.id === routeId) || null;
}

function formatRouteItinerary(/** @type {*} */ route, /** @type {*} */ cityStart, /** @type {*} */ cityEnd) {
  const start = normalizeCity(route?.city_start) || normalizeCity(cityStart);
  const end = normalizeCity(route?.city_end) || normalizeCity(cityEnd);
  const vias = Array.isArray(route?.vias) ? route.vias.map(normalizeCity).filter(Boolean) : [];
  const parts = [start, ...vias, end].filter(Boolean);

  return parts
    .filter((part, index) => index === 0 || part.toLowerCase() !== parts[index - 1].toLowerCase())
    .join(" - ");
}

function getFrenchArrivalLabel(/** @type {*} */ period) {
  const { startYear, startMonth, startDay } = period || {};
  if (!startYear) return "";

  const date = new Date(Number(startYear), Number(startMonth || 1) - 1, Number(startDay || 1));
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("fr-FR", {
    year: "numeric",
    ...(startMonth ? { month: "short" } : {}),
    ...(startDay ? { day: "numeric" } : {}),
  }).format(date).replace(/\./g, "");
}

function getAllPeriodDisplayNames(/** @type {*} */ primaryPeriodName, /** @type {*} */ primaryPeriod, /** @type {*} */ comparisonPeriods) {
  return [
    getPeriodDisplayName(primaryPeriodName, primaryPeriod),
    ...comparisonPeriods.map((/** @type {*} */ comparisonPeriod) =>
      getPeriodDisplayName(
        comparisonPeriod?.period_name ?? comparisonPeriod?.periodName ?? null,
        getComparisonPeriodValue(comparisonPeriod)
      )
    ),
  ];
}

function parseStoredMoneyValue(/** @type {*} */ value, /** @type {*} */ productType) {
  if (value == null) return null;
  const numericValue = typeof value === "number" ? value : Number(String(value).replace(/[^0-9.-]/g, ""));
  if (Number.isNaN(numericValue)) return null;
  return productType === "ROOM" ? numericValue / 2 : numericValue;
}

function mapStoredProductCostToFormProductCost(/** @type {*} */ productCost, /** @type {*} */ productType) {
  if (!productCost) {
    return {
      type: null,
      currency: null,
      value: { default: 0, single: null, twin: null, triple: null, quad: null },
      confirm: false,
    };
  }

  return {
    type: productCost.type,
    currency: productCost.cur,
    value: {
      default: parseStoredMoneyValue(productCost.value?.default, productType) ?? 0,
      single: parseStoredMoneyValue(productCost.value?.single, productType),
      twin: parseStoredMoneyValue(productCost.value?.twin, productType),
      triple: parseStoredMoneyValue(productCost.value?.triple, productType),
      quad: parseStoredMoneyValue(productCost.value?.quad, productType),
    },
    porterage: productCost.porterage,
    valueIncludesPorterage: productCost.valueIncludesPorterage,
    confirm: productCost.confirm,
    rateKind: productCost.rateKind,
  };
}

function mapLiveProductCostToFormProductCost(/** @type {*} */ productCost) {
  return {
    type: productCost.pricingType ?? productCost.type,
    currency: productCost.currency,
    value: productCost.value,
    valueIncludesPorterage: productCost.valueIncludesPorterage,
    porterage: productCost.porterage,
    confirm: productCost.confirm,
    rateKind: productCost.rateKind,
  };
}

function getSupplierFormValue(/** @type {*} */ supplierProductData, /** @type {*} */ supplierId) {
  if (!supplierId) return null;
  const supplierInfo = supplierProductData[supplierId]?.supplier;
  if (!supplierInfo) return null;

  return {
    label: supplierInfo.name,
    value: supplierId,
    foc: supplierInfo.meta?.foc,
    commission: supplierInfo.meta?.commission,
  };
}

function getProductFormValue(/** @type {*} */ supplierProductData, /** @type {*} */ supplierId, /** @type {*} */ productId) {
  if (!productId) return null;
  const productInfo = supplierProductData[supplierId]?.products?.[productId];
  if (!productInfo) return null;

  return {
    label: productInfo.name,
    value: productId,
  };
}

function getPreviousDayHotelIdFromApiCosts(/** @type {*} */ costs, /** @type {*} */ dayIndex) {
  if (dayIndex === 0 || dayIndex === null || dayIndex === undefined) return null;

  const previousDayItems = (costs || []).filter((/** @type {*} */ cost) => cost.day === dayIndex - 1);
  const room = previousDayItems.find((/** @type {*} */ item) => item.product_type === "ROOM");
  return room?.supplier_id ?? null;
}

function getComparisonProductCost(
  /** @type {*} */ env,
  /** @type {*} */ supplierProductData,
  /** @type {*} */ item,
  /** @type {*} */ period,
  /** @type {*} */ dayIndex,
  previousDayHotelId = null,
  /** @type {*} */ costOverride
) {
  const overrideIsEditable =
    costOverride?.product_cost_is_editable ?? costOverride?.isProductEditableCost ?? null;
  const overrideProductCost = costOverride?.product_cost ?? costOverride?.productCost ?? null;

  if (overrideIsEditable === true && overrideProductCost) {
    return mapOverrideProductCostToFormProductCost(overrideProductCost, item.product_type);
  }

  const product = supplierProductData[item.supplier_id]?.products?.[item.product_id];
  const useStoredEditableCost =
    overrideIsEditable !== false &&
    (item.product_cost_is_editable || !item.supplier_id || !item.product_id || !product);

  if (useStoredEditableCost) {
    return mapStoredProductCostToFormProductCost(item.product_cost, item.product_type);
  }

  if (!item.supplier_id || !item.product_id || !product) {
    return mapStoredProductCostToFormProductCost(item.product_cost, item.product_type);
  }

  const productCost = getProductCost(
    env,
    supplierProductData,
    product,
    period.startYear,
    period,
    dayIndex,
    previousDayHotelId
  );
  return mapLiveProductCostToFormProductCost(productCost);
}

function mapOverrideProductCostToFormProductCost(/** @type {*} */ productCost, /** @type {*} */ productType) {
  if (!productCost) {
    return mapStoredProductCostToFormProductCost(null, productType);
  }

  // Form-shaped overrides already store numeric values under `currency`.
  if (productCost.currency != null || typeof productCost.value?.default === "number") {
    return {
      type: productCost.type,
      currency: productCost.currency ?? productCost.cur ?? null,
      value: {
        default: productCost.value?.default ?? 0,
        single: productCost.value?.single ?? null,
        twin: productCost.value?.twin ?? null,
        triple: productCost.value?.triple ?? null,
        quad: productCost.value?.quad ?? null,
      },
      porterage: productCost.porterage,
      valueIncludesPorterage: productCost.valueIncludesPorterage,
      confirm: productCost.confirm,
      rateKind: productCost.rateKind,
    };
  }

  return mapStoredProductCostToFormProductCost(productCost, productType);
}

function getComparisonCostOverride(/** @type {*} */ comparisonPeriod, /** @type {*} */ uuid, kind = "cost") {
  if (!comparisonPeriod || !uuid) return null;
  const overrides =
    kind === "logistics"
      ? comparisonPeriod.logistics_cost_overrides ?? comparisonPeriod.logisticsCostOverrides
      : comparisonPeriod.cost_overrides ?? comparisonPeriod.costOverrides;
  return overrides?.[uuid] || null;
}

function resolveComparisonEditableFlag(/** @type {*} */ item, /** @type {*} */ costOverride) {
  if (costOverride == null) return item.product_cost_is_editable;
  return costOverride.product_cost_is_editable ?? costOverride.isProductEditableCost ?? item.product_cost_is_editable;
}

function buildComparisonPeriodFormCosts(/** @type {*} */ env, /** @type {*} */ supplierProductData, /** @type {*} */ data, /** @type {*} */ period, comparisonPeriod = null) {
  const cityRoutes = data.quote.program?.days ?? data.quote.city_routes ?? [];
  /** @type {Record<string, any[]>} */
  const costsByDay = {};

  for (let i = 0; i < data.quote.number_of_nights + 1; i++) {
    costsByDay[i] = [];
  }

  (data.costs || []).forEach((/** @type {*} */ item) => {
    const dayKey = String(item.day);
    if (!costsByDay[dayKey]) costsByDay[dayKey] = [];

    const supplier = getSupplierFormValue(supplierProductData, item.supplier_id) || {
      label: item.supplier_name,
      value: null,
      foc: null,
    };
    const product = getProductFormValue(supplierProductData, item.supplier_id, item.product_id) || {
      label: item.product_name,
      value: null,
    };
    const productType =
      supplierProductData[item.supplier_id]?.products?.[item.product_id]?.product_type || item.product_type;
    const costOverride = getComparisonCostOverride(comparisonPeriod, item.uuid, "cost");

    costsByDay[dayKey].push(
      applyShowInProgramForProductType({
        uuid: item.uuid ?? generateUUID(),
        supplier,
        supplierCity: item.supplier_city,
        product,
        productType,
        productCost: getComparisonProductCost(
          env,
          supplierProductData,
          item,
          period,
          item.day,
          getPreviousDayHotelIdFromApiCosts(data.costs, item.day),
          costOverride
        ),
        costType: item.cost_type,
        logisticsCostUUIDs: parseStoredJson(item.logistics_cost_uuids, []) || [],
        isProductEditableCost: resolveComparisonEditableFlag(item, costOverride),
        isShowInProgram: item.show_in_program,
        productProgramDescription: getProductProgramDescription(
          supplierProductData[item.supplier_id]?.products?.[item.product_id],
          period.startYear
        ),
        productProgramTitle: getProductProgramTitle(
          supplierProductData[item.supplier_id]?.products?.[item.product_id],
          period.startYear
        ),
        programSupplierName: getProgramSupplierName(supplierProductData[item.supplier_id]?.supplier),
      })
    );
  });

  const formCosts = Object.keys(costsByDay).map((day) => ({
    cityStart: cityRoutes[day]?.start,
    cityEnd: cityRoutes[day]?.end,
    routeId: cityRoutes[day]?.route_id,
    imageId: cityRoutes[day]?.image_id,
    breakfast: cityRoutes[day]?.breakfast ?? false,
    lunch: cityRoutes[day]?.lunch ?? false,
    dinner: cityRoutes[day]?.dinner ?? false,
    items: costsByDay[day],
  }));

  const formLogisticsCosts = (data.logistics_costs || []).map((/** @type {*} */ item) => {
    const supplier = getSupplierFormValue(supplierProductData, item.supplier_id) || {
      label: item.supplier_name,
      value: null,
      foc: null,
    };
    const product = getProductFormValue(supplierProductData, item.supplier_id, item.product_id) || {
      label: item.product_name,
      value: null,
    };
    const productType =
      supplierProductData[item.supplier_id]?.products?.[item.product_id]?.product_type || item.product_type;
    const costOverride = getComparisonCostOverride(comparisonPeriod, item.uuid, "logistics");

    return applyShowInProgramForProductType({
      uuid: item.uuid ?? generateUUID(),
      supplier,
      supplierCity: item.supplier_city,
      product,
      productType,
      productCost: getComparisonProductCost(env, supplierProductData, item, period, null, null, costOverride),
      days: item.days != null ? parseStoredJson(item.days, []) : productType === "TRANS" || productType === "TOUR" ? [] : [0],
      isProductEditableCost: resolveComparisonEditableFlag(item, costOverride),
      isShowInProgram: item.show_in_program,
    });
  });

  return { costs: formCosts, logisticsCosts: formLogisticsCosts };
}

function getComparisonPeriodTotalCostPax(/** @type {*} */ env, /** @type {*} */ supplierProductData, /** @type {*} */ data, /** @type {*} */ period, comparisonPeriod = null) {
  const peopleRanges = parsePeopleRanges(data.quote.people_ranges);
  const { costs, logisticsCosts } = buildComparisonPeriodFormCosts(
    env,
    supplierProductData,
    data,
    period,
    comparisonPeriod
  );

  return getTotalCosts(costs, logisticsCosts, peopleRanges);
}

function getPerPaxsByTotalCostPax(/** @type {*} */ totalCostPax, /** @type {*} */ quote, /** @type {*} */ maxLength) {
  const perPaxs = [];

  for (let i = 0; i < maxLength; i++) {
    const percent = quote.is_fixed_margin ? quote.margin_percent : quote.margin_pax[i];
    let perPax = (totalCostPax[i] * percent) / 100 + totalCostPax[i];
    perPax = roundToCustom(perPax);
    perPaxs.push(perPax);
  }

  return perPaxs;
}

function getMarginPercentForPax(/** @type {*} */ quote, /** @type {*} */ pax, /** @type {*} */ fallbackMarginPercentage) {
  if (quote?.is_fixed_margin) {
    return Number(quote.margin_percent ?? fallbackMarginPercentage) || 0;
  }

  const peopleRanges = parsePeopleRanges(quote?.people_ranges);
  const marginPax = quote?.margin_pax || [];
  const index = peopleRanges.findIndex(
    (/** @type {*} */ range) => Number(pax) >= Number(range.min) && Number(pax) <= Number(range.max)
  );

  if (index >= 0 && marginPax[index] != null) {
    return Number(marginPax[index]) || 0;
  }

  return Number(fallbackMarginPercentage) || 0;
}

function mapFormProductCostToStoredMoney(/** @type {*} */ productCost, /** @type {*} */ productType, currencySymbol = "$") {
  const wrap = (/** @type {*} */ value) => {
    if (value == null || value === "") return null;
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return null;
    const storedValue = productType === "ROOM" ? numericValue * 2 : numericValue;
    return `${currencySymbol}${storedValue}`;
  };

  return {
    type: productCost?.type ?? null,
    cur: productCost?.currency ?? productCost?.cur ?? null,
    value: {
      default: wrap(productCost?.value?.default) ?? `${currencySymbol}0`,
      single: wrap(productCost?.value?.single),
      twin: wrap(productCost?.value?.twin),
      triple: wrap(productCost?.value?.triple),
      quad: wrap(productCost?.value?.quad),
    },
    porterage: productCost?.porterage,
    valueIncludesPorterage: productCost?.valueIncludesPorterage,
    confirm: productCost?.confirm,
    rateKind: productCost?.rateKind,
  };
}

function resolveItemizedExportComparisonPeriod(/** @type {*} */ data, /** @type {*} */ periodKey) {
  if (!periodKey || periodKey === "primary") return null;
  const prefix = "comparison-";
  if (!String(periodKey).startsWith(prefix)) return null;

  const index = Number(String(periodKey).slice(prefix.length));
  if (Number.isNaN(index)) return null;

  const comparisonPeriods = parseComparisonPeriods(data?.quote?.comparison_periods);
  return comparisonPeriods[index] || null;
}

function buildItemizedExportData(/** @type {*} */ env, /** @type {*} */ supplierProductData, /** @type {*} */ data, comparisonPeriod = null) {
  if (!comparisonPeriod) return data;

  const period = getComparisonPeriodValue(comparisonPeriod);
  const { costs: formCosts, logisticsCosts: formLogisticsCosts } = buildComparisonPeriodFormCosts(
    env,
    supplierProductData,
    data,
    period,
    comparisonPeriod
  );
  const currencySymbol = moneyService.getCurrencySource(env)?.currency_symbol || "$";
  const marginFields = resolveComparisonPeriodMarginFields(comparisonPeriod, data.quote);

  const costs = formCosts.flatMap((/** @type {*} */ day, /** @type {number} */ dayIndex) =>
    (day.items || []).map((/** @type {*} */ item, /** @type {number} */ order) => ({
      uuid: item.uuid,
      day: dayIndex,
      order_in_day: order,
      supplier_id: item.supplier?.value ?? null,
      supplier_name: item.supplier?.value ? null : item.supplier?.label,
      product_id: item.product?.value ?? null,
      product_name: item.product?.value ? null : item.product?.label,
      product_type: item.productType,
      cost_type: item.costType,
      logistics_cost_uuids: item.logisticsCostUUIDs || [],
      product_cost: mapFormProductCostToStoredMoney(item.productCost, item.productType, currencySymbol),
    }))
  );

  const logistics_costs = formLogisticsCosts.map((/** @type {*} */ item) => ({
    uuid: item.uuid,
    supplier_id: item.supplier?.value ?? null,
    supplier_name: item.supplier?.value ? null : item.supplier?.label,
    product_id: item.product?.value ?? null,
    product_name: item.product?.value ? null : item.product?.label,
    product_type: item.productType,
    days: JSON.stringify(item.days || []),
    product_cost: mapFormProductCostToStoredMoney(item.productCost, item.productType, currencySymbol),
  }));

  return {
    ...data,
    costs,
    logistics_costs,
    quote: {
      ...data.quote,
      ...marginFields,
      start_date: {
        year: period.startYear,
        month: period.startMonth,
        day: period.startDay,
      },
      end_date: {
        year: period.endYear,
        month: period.endMonth,
        day: period.endDay,
      },
      period_name: comparisonPeriod.period_name ?? comparisonPeriod.periodName ?? data.quote.period_name,
    },
  };
}

function getComparisonPeriodPerPaxs(/** @type {*} */ env, /** @type {*} */ supplierProductData, /** @type {*} */ data, /** @type {*} */ comparisonPeriod, /** @type {*} */ maxLength) {
  const period = getComparisonPeriodValue(comparisonPeriod);
  const totalCostPax = getComparisonPeriodTotalCostPax(env, supplierProductData, data, period, comparisonPeriod);
  const marginFields = resolveComparisonPeriodMarginFields(comparisonPeriod, data.quote);
  return getPerPaxsByTotalCostPax(totalCostPax, marginFields, maxLength);
}

function resolveComparisonPeriodMarginFields(/** @type {*} */ comparisonPeriod, /** @type {*} */ quote) {
  const margin = comparisonPeriod?.margin;
  if (!margin) {
    return {
      is_fixed_margin: quote.is_fixed_margin,
      margin_percent: quote.margin_percent,
      margin_pax: quote.margin_pax || [],
    };
  }

  const status = margin.status;
  const percentFixed = margin.percentFixed ?? margin.percent_fixed ?? margin.margin_percent;
  const percentDynamic = margin.percentDynamic || margin.percent_dynamic || margin.margin_pax || [];

  return {
    is_fixed_margin: status ? status === "fixed" : Boolean(margin.is_fixed_margin),
    margin_percent: percentFixed,
    margin_pax: Array.isArray(percentDynamic) ? percentDynamic : [],
  };
}

function getMarginPercentageFromFields(/** @type {*} */ marginFields, /** @type {any[]} */ peopleRanges = []) {
  if (marginFields.is_fixed_margin) return marginFields.margin_percent ?? 0;

  const maxRangeIndex = (peopleRanges || []).reduce((maxIndex, current, index, array) => {
    return Number(current.min) > Number(array[maxIndex]?.min || 0) ? index : maxIndex;
  }, 0);

  return marginFields.margin_pax?.[maxRangeIndex] ?? marginFields.margin_pax?.[0] ?? 0;
}

function getExcelNumberOrBlank(/** @type {*} */ value) {
  return value === null || value === undefined || value === "" || Number.isNaN(Number(value)) ? "" : Number(value);
}

function isSupplementOverrideSet(/** @type {*} */ value) {
  return value != null && value !== "" && !Number.isNaN(Number(value));
}

function getSupplementOverridesFromSource(/** @type {*} */ source = {}) {
  return {
    triple: source.supplement_triple_override ?? source.supplementTripleOverride ?? null,
    quad: source.supplement_quad_override ?? source.supplementQuadOverride ?? null,
  };
}

function getSupplementOverridesFromQuote(/** @type {*} */ quote = {}) {
  return getSupplementOverridesFromSource(quote);
}

function getSupplementDisplayValue(/** @type {*} */ calculated, /** @type {*} */ override, /** @type {*} */ roundFn) {
  if (isSupplementOverrideSet(override)) return Number(override);
  return roundFn(calculated);
}

function applySupplementOverrides(/** @type {*} */ supplements, /** @type {*} */ overrides = {}) {
  return {
    ...supplements,
    triple: isSupplementOverrideSet(overrides.triple) ? Number(overrides.triple) : supplements.triple,
    quad: isSupplementOverrideSet(overrides.quad) ? Number(overrides.quad) : supplements.quad,
    tripleIsOverride: isSupplementOverrideSet(overrides.triple),
    quadIsOverride: isSupplementOverrideSet(overrides.quad),
  };
}

function getStoredRoomSupplementValues(/** @type {*} */ costs, /** @type {*} */ logisticsCosts, /** @type {*} */ marginPercentage, /** @type {*} */ discountMargin, /** @type {*} */ overrides = {}) {
  let totalCost = 0;
  let totalCostSingle = 0;
  let totalCostTwin = 0;
  let totalCostTriple = 0;
  let totalCostQuad = 0;

  [...costs, ...logisticsCosts].forEach((cost) => {
    if (cost.product_type === "ROOM") {
      const productCost = Number(cost.product_cost.value.default.substring(1));
      totalCost += productCost;
      totalCostSingle += cost.product_cost.value.single
        ? Number(cost.product_cost.value.single.substring(1))
        : productCost;
      totalCostTwin += cost.product_cost.value.twin ? Number(cost.product_cost.value.twin.substring(1)) : productCost;
      totalCostTriple += cost.product_cost.value.triple
        ? Number(cost.product_cost.value.triple.substring(1))
        : productCost;
      totalCostQuad += cost.product_cost.value.quad ? Number(cost.product_cost.value.quad.substring(1)) : productCost;
    }
  });

  return applySupplementOverrides(
    getRoomSupplementValues(
      { default: totalCost, single: totalCostSingle, twin: totalCostTwin, triple: totalCostTriple, quad: totalCostQuad },
      marginPercentage,
      discountMargin
    ),
    overrides
  );
}

function getFormRoomSupplementValues(/** @type {*} */ costs, /** @type {*} */ logisticsCosts, /** @type {*} */ marginPercentage, /** @type {*} */ discountMargin, /** @type {*} */ overrides = {}) {
  const totalCost = { default: 0, single: 0, twin: 0, triple: 0, quad: 0 };

  const costItems = costs.flatMap((/** @type {*} */ cost) => cost.items || []);
  [...costItems, ...logisticsCosts].forEach((item) => {
    if (item.productType === "ROOM") {
      const productCost = item.productCost.value;
      const roomMultiplier = 2;
      const defaultValue = (Number(productCost.default) || 0) * roomMultiplier;

      totalCost.default += defaultValue;
      totalCost.single += productCost.single != null ? Number(productCost.single) * roomMultiplier : defaultValue;
      totalCost.twin += productCost.twin != null ? Number(productCost.twin) * roomMultiplier : defaultValue;
      totalCost.triple += productCost.triple != null ? Number(productCost.triple) * roomMultiplier : defaultValue;
      totalCost.quad += productCost.quad != null ? Number(productCost.quad) * roomMultiplier : defaultValue;
    }
  });

  return applySupplementOverrides(
    getRoomSupplementValues(totalCost, marginPercentage, discountMargin),
    overrides
  );
}

function getRoomSupplementValues(/** @type {*} */ totalCost, /** @type {*} */ marginPercentage, /** @type {*} */ discountMargin) {
  const single = totalCost.single - totalCost.default / 2;
  const twin = (totalCost.twin / 2 - totalCost.default / 2) * (1 + marginPercentage / 100);
  const triple = (totalCost.triple / 3 - totalCost.default / 2) * (1 - discountMargin / 100);
  const quad = (totalCost.quad / 4 - totalCost.default / 2) * (1 - discountMargin / 100);
  const child = single * (1 - marginPercentage / 100) * -1;

  return { single, twin, triple, quad, child };
}

function getComparisonPeriodSupplementValues(/** @type {*} */ env, /** @type {*} */ supplierProductData, /** @type {*} */ data, /** @type {*} */ comparisonPeriod, /** @type {*} */ marginPercentage = undefined) {
  const period = getComparisonPeriodValue(comparisonPeriod);
  const { costs, logisticsCosts } = buildComparisonPeriodFormCosts(
    env,
    supplierProductData,
    data,
    period,
    comparisonPeriod
  );
  const marginFields = resolveComparisonPeriodMarginFields(comparisonPeriod, data.quote);
  const peopleRanges = parsePeopleRanges(data.quote.people_ranges);
  const resolvedMarginPercentage =
    marginPercentage ?? getMarginPercentageFromFields(marginFields, peopleRanges);

  return getFormRoomSupplementValues(
    costs,
    logisticsCosts,
    resolvedMarginPercentage,
    data.quote.discount_margin,
    getSupplementOverridesFromSource(comparisonPeriod)
  );
}

function getQuoteOverviewPriceTable(/** @type {*} */ env, /** @type {*} */ supplierProductData, /** @type {*} */ data) {
  const peopleRanges = parsePeopleRanges(data?.quote?.people_ranges);
  const peopleRangesLength = peopleRanges.length;
  const totalCostPax = Array.isArray(data?.quote?.total_cost_pax) ? data.quote.total_cost_pax : [];
  const perPaxs = getPerPaxsByTotalCostPax(totalCostPax, data.quote, peopleRangesLength);
  const comparisonPeriods = parseComparisonPeriods(data.quote.comparison_periods);
  const comparisonPeriodPerPaxs = comparisonPeriods.map((comparisonPeriod) =>
    getComparisonPeriodPerPaxs(env, supplierProductData, data, comparisonPeriod, peopleRangesLength)
  );
  const comparisonPeriodSupplements = comparisonPeriods.map((comparisonPeriod) =>
    getComparisonPeriodSupplementValues(env, supplierProductData, data, comparisonPeriod)
  );
  const primaryPeriod = mapApiDateToPeriod(data.quote.start_date, data.quote.end_date);
  const periodHeaders = getAllPeriodDisplayNames(
    data.quote.period_name ?? data.quote.periodName ?? data.quote.program?.period_name,
    primaryPeriod,
    comparisonPeriods
  );
  const marginPercentage = getMarginPercentageFromFields(
    {
      is_fixed_margin: data.quote.is_fixed_margin,
      margin_percent: data.quote.margin_percent,
      margin_pax: data.quote.margin_pax || [],
    },
    peopleRanges
  );
  const supplements = getStoredRoomSupplementValues(
    data.costs,
    data.logistics_costs,
    marginPercentage,
    data.quote.discount_margin,
    getSupplementOverridesFromQuote(data.quote)
  );

  const peopleRows = peopleRanges.map((range, index) => ({
    label: String(range.min ?? ""),
    values: [
      getExcelNumberOrBlank(perPaxs[index]),
      ...comparisonPeriodPerPaxs.map((column) => getExcelNumberOrBlank(column[index])),
    ],
  }));

  const supplementRows = [
    {
      key: "single",
      label: "Supplément individuel",
      values: [
        roundUpTo5(supplements.single),
        ...comparisonPeriodSupplements.map((supplement) => roundUpTo5(supplement.single)),
      ],
    },
    {
      key: "triple",
      label: "Réduction triple",
      values: [
        supplements.tripleIsOverride ? supplements.triple : roundDownTo5(supplements.triple),
        ...comparisonPeriodSupplements.map((supplement) =>
          supplement.tripleIsOverride ? supplement.triple : roundDownTo5(supplement.triple)
        ),
      ],
    },
    {
      key: "quad",
      label: "Réduction quad.",
      values: [
        supplements.quadIsOverride ? supplements.quad : roundDownTo5(supplements.quad),
        ...comparisonPeriodSupplements.map((supplement) =>
          supplement.quadIsOverride ? supplement.quad : roundDownTo5(supplement.quad)
        ),
      ],
    },
    {
      key: "child",
      label: "Réduction enfant",
      values: [
        roundDownTo5(supplements.child),
        ...comparisonPeriodSupplements.map((supplement) => roundDownTo5(supplement.child)),
      ],
    },
  ];

  return {
    year: primaryPeriod.startYear || "",
    periodHeaders,
    peopleRows,
    supplementRows,
  };
}

function writeComparisonPeriodValues(/** @type {*} */ worksheet, /** @type {*} */ sourceCell, /** @type {*} */ values, /** @type {*} */ formatValue) {
  _applyPriceNumberFormat(sourceCell);
  values.forEach((/** @type {*} */ value, /** @type {*} */ index) => {
    const targetCol = sourceCell.col + (index + 1) * 2;
    const cell = worksheet.getCell(sourceCell.row, targetCol);
    cell.value = formatValue(value);
    cell.style = { ...sourceCell.style };
    cell.border = { ...sourceCell.border };
    cell.fill = sourceCell.fill;
    cell.alignment = sourceCell.alignment;
    _applyPriceNumberFormat(cell);
    worksheet.getColumn(targetCol).width = worksheet.getColumn(sourceCell.col).width;
  });
}

function applySupplementTableBorders(/** @type {*} */ worksheet, /** @type {*} */ startRow, /** @type {*} */ endRow, /** @type {*} */ endCol, /** @type {*} */ valueCols, { hasHeaderRow = true } = {}) {
  const bodyStartRow = hasHeaderRow ? startRow + 1 : startRow;
  const bodyLeftBorder = worksheet.getCell(bodyStartRow, 1).border.left;
  const bodyRightBorder = worksheet.getCell(bodyStartRow, 3).border.right;
  const bottomBorder = worksheet.getCell(endRow, 1).border.bottom;
  const valueColSet = new Set(valueCols);

  for (let col = 1; col <= endCol; col++) {
    for (let row = bodyStartRow; row <= endRow; row++) {
      const cell = worksheet.getCell(row, col);
      cell.border = {};
      if (col > 3 && !valueColSet.has(col)) {
        cell.value = null;
        cell.numFmt = undefined;
      }
    }
  }

  if (hasHeaderRow) {
    const headerBorder = worksheet.getCell(startRow, 1).border;
    worksheet.getCell(startRow, 1).border = {
      top: headerBorder.top,
      left: headerBorder.left,
      right: bodyRightBorder,
      bottom: headerBorder.bottom,
    };
  }

  for (let col = 1; col <= endCol; col++) {
    for (let row = bodyStartRow; row <= endRow; row++) {
      const cell = worksheet.getCell(row, col);
      cell.style = {
        ...(cell.style || {}),
      };
      cell.border = {
        ...(cell.border || {}),
        ...(col === 1 ? { left: bodyLeftBorder } : {}),
        ...(col === endCol ? { right: bodyRightBorder } : {}),
        ...(row === endRow ? { bottom: bottomBorder } : {}),
      };
    }
  }
}

function getFOCInfo(/** @type {*} */ costs, /** @type {*} */ logisticsCosts, /** @type {*} */ pax) {
  const numberOfClients = Number(pax);

  let tempCounter = 0;
  let totalVariableCosts = 0;
  let totalFixedCosts = 0;
  let totalFoc = 0;
  /** @type {Record<string, { name: *, products: Record<string, *> }>} */
  const focInfo = {};

  const logisticsUUIDs = logisticsCosts.map((/** @type {*} */ cost) => cost.uuid);

  costs.forEach((/** @type {*} */ cost) => {
    const variableCosts = cost.items.filter((/** @type {*} */ item) => ["PP"].includes(item.costType));
    const fixedCosts = cost.items.filter((/** @type {*} */ item) => ["PG", "PD"].includes(item.costType));

    variableCosts.forEach((/** @type {*} */ item) => {
      const supplierId = item.supplier?.value;
      if (!supplierId) return;
      const supplierKey = String(supplierId);

      const supplierName = item.supplier.label;
      const productId = item.product?.value || `product_id_temp_${tempCounter++}`; // In case the user enters a custom product
      const productKey = String(productId);
      const productName = item.product?.label;
      const productType = item.productType;
      const productCost = Number(item.productCost.value.default) || 0;
      const matchedSupplierCount = countMatchedLogisticsSuppliers(item.logisticsCostUUIDs, logisticsUUIDs);
      const { groupTotal, focQuantity } = getVariableCostAllocation(
        productCost,
        numberOfClients,
        matchedSupplierCount,
        productType,
        item.supplier?.foc
      );
      totalVariableCosts += groupTotal;

      if (focQuantity === 0) return;

      const focValue = focQuantity * productCost;

      if (!focInfo[supplierKey]) {
        focInfo[supplierKey] = {
          name: supplierName,
          products: {},
        };
      }

      let product = focInfo[supplierKey].products[productKey];

      if (product) {
        product.quantity += focQuantity;
        product.value += focValue;
      } else {
        focInfo[supplierKey].products[productKey] = {
          id: productId,
          name: productName,
          price: item.productCost.value.default,
          quantity: focQuantity,
          value: focValue,
        };
      }

      totalFoc += focValue;
    });

    fixedCosts.forEach((/** @type {*} */ item) => {
      totalFixedCosts += Number(item.productCost.value.default);
    });
  });

  logisticsCosts.forEach((/** @type {*} */ cost) => {
    const numberOfDays = (cost.days || []).length;
    const total = Number(cost.productCost.value.default) * numberOfDays;
    totalFixedCosts += total;
  });

  return {
    total: (totalVariableCosts + totalFixedCosts) / numberOfClients,
    totalFoc: totalFoc,
    focInfo: focInfo,
  };
}

function getProductCost(/** @type {*} */ env, /** @type {*} */ suppliers, /** @type {*} */ product, /** @type {*} */ rateYear, /** @type {*} */ period, /** @type {*} */ dayIndex, previousDayHotelId = null) {
  const rateHistory = product.rate_history.find((/** @type {*} */ history) => history.rate_year === rateYear);

  if (!rateHistory) {
    const source = moneyService.getCurrencySource(env);
    const currencySymbol = source.currency;
    return {
      type: null,
      currency: currencySymbol,
      value: { default: 0, single: null, triple: null, twin: null, quad: null },
      confirm: false,
    };
  }

  const isSeasonal = rateHistory.seasonal === 1;

  let defaultRate, singleRate, twinRate, tripleRate, quadRate;

  if (isSeasonal) {
    // Get rate for the given day
    const { startYear, startMonth, startDay } = period;
    const date = _getDateByDayIndex(startYear, startMonth ?? 1, startDay ?? 1, dayIndex ?? 0);
    const month = date.getMonth() + 1;
    const day = date.getDate();

    const defaultRoomTypes = [undefined, "DOUBLE_QK", "DEFAULT"]; // Default room types

    defaultRate = rateHistory.rate.find(
      (/** @type {*} */ rate) => _isInDateRange(rate, month, day, month, day) && defaultRoomTypes.includes(rate.roomType)
    )?.rate;
    singleRate = rateHistory.rate.find(
      (/** @type {*} */ rate) => _isInDateRange(rate, month, day, month, day) && rate.roomType === "SINGLE_DK"
    )?.rate;
    twinRate = rateHistory.rate.find(
      (/** @type {*} */ rate) => _isInDateRange(rate, month, day, month, day) && rate.roomType === "DOUBLE_DOUBLE"
    )?.rate;
    tripleRate = rateHistory.rate.find(
      (/** @type {*} */ rate) => _isInDateRange(rate, month, day, month, day) && rate.roomType === "TRIPLE"
    )?.rate;
    quadRate = rateHistory.rate.find(
      (/** @type {*} */ rate) => _isInDateRange(rate, month, day, month, day) && rate.roomType === "QUAD"
    )?.rate;
  } else {
    defaultRate = rateHistory.rate;
    singleRate = rateHistory.meta?.per_room?.find((/** @type {*} */ perRoom) => perRoom.type.includes("SINGLE_DK"));
    twinRate = rateHistory.meta?.per_room?.find((/** @type {*} */ perRoom) => perRoom.type.includes("DOUBLE_DOUBLE"));
    tripleRate = rateHistory.meta?.per_room?.find((/** @type {*} */ perRoom) => perRoom.type.includes("TRIPLE"));
    quadRate = rateHistory.meta?.per_room?.find((/** @type {*} */ perRoom) => perRoom.type.includes("QUAD"));
  }

  const tax = rateHistory.meta.custom_tax ? rateHistory.meta.custom_tax : _getSupplierTax(suppliers, product);

  let defaultValue =
    defaultRate != null
      ? ProductService.calcTotal({
          rateInfo: defaultRate,
          tax,
          taxInclusive: rateHistory.tax_inclusive === 1,
          extraList: _getExtraList(rateHistory),
          occupancy: _getOccupancy(suppliers, product, rateHistory),
          resort_fee: _getResortFee(suppliers, product),
          environment: env,
        })
      : null;

  let singleValue =
    singleRate != null
      ? ProductService.calcTotal({
          rateInfo: singleRate,
          tax,
          taxInclusive: rateHistory.tax_inclusive === 1,
          extraList: _getExtraList(rateHistory),
          occupancy: _getOccupancy(suppliers, product, rateHistory),
          resort_fee: _getResortFee(suppliers, product),
          environment: env,
        })
      : null;

  let twinValue =
    twinRate != null
      ? ProductService.calcTotal({
          rateInfo: twinRate,
          tax,
          taxInclusive: rateHistory.tax_inclusive === 1,
          extraList: _getExtraList(rateHistory),
          occupancy: _getOccupancy(suppliers, product, rateHistory),
          resort_fee: _getResortFee(suppliers, product),
          environment: env,
        })
      : null;

  let tripleValue =
    tripleRate != null
      ? ProductService.calcTotal({
          rateInfo: tripleRate,
          tax,
          taxInclusive: rateHistory.tax_inclusive === 1,
          extraList: _getExtraList(rateHistory),
          occupancy: _getOccupancy(suppliers, product, rateHistory),
          resort_fee: _getResortFee(suppliers, product),
          environment: env,
        })
      : null;

  let quadValue =
    quadRate != null
      ? ProductService.calcTotal({
          rateInfo: quadRate,
          tax,
          taxInclusive: rateHistory.tax_inclusive === 1,
          extraList: _getExtraList(rateHistory),
          occupancy: _getOccupancy(suppliers, product, rateHistory),
          resort_fee: _getResortFee(suppliers, product),
          environment: env,
        })
      : null;

  const isRoom = product.product_type === "ROOM";

  if (isRoom) {
    defaultValue = defaultValue != null ? defaultValue / 2 : null;
    singleValue = singleValue != null ? singleValue / 2 : null;
    tripleValue = tripleValue != null ? tripleValue / 2 : null;
    twinValue = twinValue != null ? twinValue / 2 : null;
    quadValue = quadValue != null ? quadValue / 2 : null;
  }

  const porterageMeta = suppliers[product.supplier_id]?.supplier?.meta?.porterage;

  const isPorterageIncluded =
    isRoom && porterageMeta?.text === "mandatory" && previousDayHotelId !== product.supplier_id;

  let porterage;

  if (isPorterageIncluded) {
    porterage = moneyService.stripFormatting(porterageMeta.value) || 0;

    defaultValue = defaultValue != null ? defaultValue + porterage : null;
    singleValue = singleValue != null ? singleValue + porterage : null;
    tripleValue = tripleValue != null ? tripleValue + porterage : null;
    twinValue = twinValue != null ? twinValue + porterage : null;
    quadValue = quadValue != null ? quadValue + porterage : null;
  }

  const rateKind = rateHistory.meta?.rate_kind;

  return {
    type: defaultRate?.type,
    pricingType: isSeasonal ? "seasonal" : "flat",
    currency: defaultRate?.cur,
    value: {
      default: defaultValue != null ? moneyService._num(defaultValue) : null,
      single: singleValue != null ? moneyService._num(singleValue) : null,
      twin: twinValue != null ? moneyService._num(twinValue) : null,
      triple: tripleValue != null ? moneyService._num(tripleValue) : null,
      quad: quadValue != null ? moneyService._num(quadValue) : null,
    },
    ...(isPorterageIncluded && {
      porterage,
      valueIncludesPorterage: isPorterageIncluded,
    }),
    confirm: rateHistory.confirmed === 1,
    costType: defaultRate?.type === "PROOM" ? "PP" : defaultRate?.type,
    ...(rateKind && { rateKind }),
  };
}

function getProductRateForYear(/** @type {*} */ product, /** @type {*} */ rateYear) {
  if (!product?.rate_history) return null;
  return product.rate_history.find((/** @type {*} */ history) => history.rate_year === rateYear) || null;
}

function getProductProgramDescription(/** @type {*} */ product, /** @type {*} */ rateYear) {
  const description = getProductRateForYear(product, rateYear)?.program_description ?? "";
  // Ignore bullet-only placeholder previously prefilled by the product form
  return description.trim() === "-" ? "" : description;
}

function getProductProgramTitle(/** @type {*} */ product, /** @type {*} */ rateYear) {
  return String(getProductRateForYear(product, rateYear)?.program_title ?? "").trim();
}

function getProgramSupplierName(/** @type {*} */ supplier) {
  return String(supplier?.meta?.program_supplier_name ?? "").trim();
}

function formatProgramWebsiteUrl(/** @type {*} */ website) {
  const raw = String(website || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

/**
 * Unique ROOM accommodations in quote appearance order (costs days, then logistics).
 * @param {*} quote
 * @param {Record<string, *>} [supplierProductData]
 * @returns {{ key: string, description: string, name: string, website: string, websiteHref: string }[]}
 */
function getProgramAccommodations(quote, supplierProductData = {}) {
  /** @type {{ key: string, description: string, name: string, website: string, websiteHref: string }[]} */
  const result = [];
  const seen = new Set();

  const pushRoomItem = (/** @type {*} */ item) => {
    if (!item || item.productType !== "ROOM") return;

    const supplierId = item.supplier?.value ?? null;
    const supplierInfo = supplierId != null ? supplierProductData?.[String(supplierId)]?.supplier : null;
    const description = String(item.productProgramDescription || "").trim();
    // Hébergements standards shows the real supplier name (not program_supplier_name).
    const baseName =
      String(supplierInfo?.name || "").trim() ||
      String(item.supplier?.label || "").trim();
    const hotelClass = supplierInfo?.meta?.hotel_class;
    const starSuffix =
      hotelClass != null && hotelClass !== "" ? ` ${"*".repeat(Number(hotelClass))}` : "";
    const name = baseName ? `${baseName}${starSuffix}` : "";
    const website = String(supplierInfo?.website || "").trim();

    if (!description && !name && !website) return;

    const key =
      supplierId != null
        ? `supplier:${supplierId}`
        : `manual:${description.toLowerCase()}|${baseName.toLowerCase()}|${website.toLowerCase()}`;
    if (seen.has(key)) return;
    seen.add(key);

    result.push({
      key,
      description,
      name,
      website,
      websiteHref: formatProgramWebsiteUrl(website),
    });
  };

  (quote?.costs || []).forEach((/** @type {*} */ day) => {
    (day?.items || []).forEach(pushRoomItem);
  });
  (quote?.logisticsCosts || []).forEach(pushRoomItem);

  return result;
}

function _getSupplierTax(/** @type {*} */ suppliers, /** @type {*} */ product) {
  const supplier = suppliers[product.supplier_id].supplier;
  return supplier ? supplier.meta.tax : 0;
}

// Get Occupancy
function _getOccupancy(/** @type {*} */ suppliers, /** @type {*} */ product, /** @type {*} */ rateHistory) {
  if (!(_showOccupancy(suppliers, product) || _customOccupancyIsVisible(product, rateHistory))) return null;
  return _occupancy(suppliers, product);
}

function _showOccupancy(/** @type {*} */ suppliers, /** @type {*} */ product) {
  return product.product_type === "ROOM" && _occupancy(suppliers, product).value && !product.tax_inclusive;
}

function _occupancy(/** @type {*} */ suppliers, /** @type {*} */ product) {
  const supplier = suppliers[product.supplier_id].supplier;
  return supplier ? supplier.meta.occupancy_rate || {} : {};
}

function _customOccupancyIsVisible(/** @type {*} */ product, /** @type {*} */ rateHistory) {
  return _showCustomOccupancy(product) && _customOccupancy(product, rateHistory) !== false;
}

function _customOccupancy(/** @type {*} */ product, /** @type {*} */ rateHistory) {
  return rateHistory.meta.custom_occupancy ?? false;
}

function _showCustomOccupancy(/** @type {*} */ product) {
  return product.product_type === "ROOM";
}

// Get Resort Fee
function _getResortFee(/** @type {*} */ suppliers, /** @type {*} */ product) {
  return _showResortFee(suppliers, product) ? _resortFee(suppliers, product) : null;
}

function _resortFee(/** @type {*} */ suppliers, /** @type {*} */ product) {
  const supplier = suppliers[product.supplier_id].supplier;
  return supplier ? supplier.meta.resort_fee || {} : {};
}

function _showResortFee(/** @type {*} */ suppliers, /** @type {*} */ product) {
  return product.product_type === "ROOM" && _resortFee(suppliers, product).value && !product.tax_inclusive;
}

// Get Extra List
function _getExtraList(/** @type {*} */ rateHistory) {
  return Object.keys(rateHistory.meta)
    .filter((key) => key.includes("extra_"))
    .map((key) => ({
      key,
      ...rateHistory.meta[key],
    }));
}

function _getCellByValue(/** @type {*} */ worksheet, /** @type {*} */ value) {
  const values = worksheet.getSheetValues();

  for (let r = 1; r < values.length; r++) {
    const row = values[r];
    if (!row) continue;

    for (let c = 1; c < row.length; c++) {
      if (row[c] === value) {
        return worksheet.getCell(r, c);
      }
    }
  }

  return null;
}

function _duplicateRowsAndDetachStyles(/** @type {*} */ worksheet, /** @type {*} */ rowNumber, /** @type {*} */ amount) {
  worksheet.duplicateRow(rowNumber, amount, true);
  for (let i = 0; i < amount; i++) {
    const row = worksheet.getRow(rowNumber + i);
    row.eachCell({ includeEmpty: true }, (/** @type {*} */ cell) => {
      cell.style = JSON.parse(JSON.stringify(cell.style || {}));
    });
  }
}

const DOC_END_COL = 7;
const QUOTE_HEADER_ROWS_START = 2;
const QUOTE_HEADER_ROWS_END = 6;
const QUOTE_HEADER_ROW_HEIGHT = 18;
const EMU_PER_PIXEL_AT_96_DPI = 9525;
const EMU_PER_POINT = 12700;

function _getHeight(/** @type {*} */ worksheet, /** @type {*} */ cell, widthReduction = 0, charFactor = 1.1) {
  const text = cell.value ? cell.value.toString() : "";
  if (!text) return 1;

  let totalWidth = 0;
  if (cell.isMerged) {
    const master = cell.master;
    const mergeRange = (worksheet.model.merges || []).find((/** @type {*} */ m) => m.startsWith(`${master.address}:`));
    const endCol = mergeRange ? worksheet.getCell(mergeRange.split(":")[1]).col : master.col;

    for (let i = master.col; i <= endCol; i++) {
      totalWidth += worksheet.getColumn(i).width || 10;
    }
  } else {
    totalWidth = worksheet.getColumn(cell.col).width || 10;
  }

  const usableWidth = Math.max(1, totalWidth - widthReduction);
  const charsPerLine = usableWidth * charFactor;

  return Math.ceil(text.length / charsPerLine);
}

function _captureCellAppearance(/** @type {*} */ cell) {
  return {
    style: cell.style ? JSON.parse(JSON.stringify(cell.style)) : {},
    alignment: cell.alignment ? { ...cell.alignment } : {},
    fill: cell.fill,
    border: cell.border ? { ...cell.border } : undefined,
  };
}

function _applyCellAppearance(/** @type {*} */ targetCell, /** @type {*} */ appearance, alignmentOverrides = {}) {
  targetCell.style = {
    ...appearance.style,
    alignment: {
      ...appearance.alignment,
      ...alignmentOverrides,
    },
  };

  if (appearance.fill) {
    targetCell.fill = appearance.fill;
  }

  if (appearance.border) {
    targetCell.border = appearance.border;
  }
}

function formatQuoteDuration(/** @type {*} */ numberOfNights) {
  const nights = Number(numberOfNights);
  if (!Number.isFinite(nights) || nights < 0) return "";

  const days = nights + 2;
  const paddedNights = String(nights).padStart(2, "0");
  const paddedDays = String(days).padStart(2, "0");
  return `${paddedNights} nuits / ${paddedDays} jours`;
}

function buildQuoteFolderName(/** @type {*} */ title, /** @type {*} */ numberOfNights) {
  const trimmedTitle = String(title || "").trim();
  if (!trimmedTitle) return "";
  if (numberOfNights === null || numberOfNights === undefined || numberOfNights === "") return "";

  const nights = Number(numberOfNights);
  if (!Number.isFinite(nights) || nights < 0) return "";

  const days = nights + 2;
  const paddedDays = String(days).padStart(2, "0");
  const paddedNights = String(nights).padStart(2, "0");
  return `${trimmedTitle} - ${paddedDays} jours / ${paddedNights} nuits`;
}

function _fillHeaderField(/** @type {*} */ worksheet, /** @type {*} */ placeholder, /** @type {*} */ value, { wrapText = false } = {}) {
  const cell = _getCellByValue(worksheet, placeholder);
  if (!cell) return null;

  const templateAppearance = _captureCellAppearance(cell);
  cell.value = value ?? "";
  _applyCellAppearance(cell, templateAppearance, {
    wrapText,
    vertical: templateAppearance.alignment?.vertical || "middle",
    horizontal: templateAppearance.alignment?.horizontal || "center",
  });
  return cell;
}

function _programTitleLineCount(/** @type {*} */ text, /** @type {*} */ charsPerLine) {
  const width = Math.max(1, Math.floor(charsPerLine));
  const words = String(text).trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 1;

  let lines = 1;
  let used = 0;
  for (const word of words) {
    if (!used) {
      used = Math.min(word.length, width);
      if (word.length > width) lines += Math.ceil(word.length / width) - 1;
      continue;
    }

    if (used + 1 + word.length <= width) {
      used += 1 + word.length;
      continue;
    }

    lines += 1;
    used = Math.min(word.length, width);
  }

  return lines;
}

function _applyProgramTitleRowHeight(/** @type {*} */ worksheet, /** @type {*} */ titleCell) {
  if (!titleCell) return;

  const text = titleCell.value ? String(titleCell.value) : "";
  if (!text.trim()) return;

  const columnWidth = worksheet.getColumn(titleCell.col).width || 10;
  // Stay close to the real column fit so a short title keeps a single-line row.
  const charsPerLine = Math.max(8, columnWidth * 0.9);
  const lineCount = _programTitleLineCount(text, charsPerLine);
  if (lineCount <= 1) return;

  worksheet.getRow(titleCell.row).height = lineCount * QUOTE_HEADER_ROW_HEIGHT;
}

function _fillArrivalField(/** @type {*} */ worksheet, /** @type {*} */ value) {
  const cell = _getCellByValue(worksheet, "[start-date]");
  if (!cell) return;

  const templateAppearance = _captureCellAppearance(cell);
  cell.value = value ?? "";
  _applyCellAppearance(cell, templateAppearance, {
    wrapText: false,
    vertical: templateAppearance.alignment?.vertical || "middle",
    horizontal: templateAppearance.alignment?.horizontal || "left",
  });
}

function _normalizeQuoteHeaderRowsHeight(/** @type {*} */ worksheet) {
  for (let row = QUOTE_HEADER_ROWS_START; row <= QUOTE_HEADER_ROWS_END; row++) {
    worksheet.getRow(row).height = QUOTE_HEADER_ROW_HEIGHT;
  }
}

function _applyWrappedRowHeight(/** @type {*} */ worksheet, /** @type {*} */ rowIndex, /** @type {*} */ colIndex, widthReduction = 0) {
  const cell = worksheet.getCell(rowIndex, colIndex);
  worksheet.getRow(rowIndex).height = _getHeight(worksheet, cell, widthReduction) * 16;
}

function _fillMergeAndHeight(/** @type {*} */ worksheet, /** @type {*} */ rowIndex, /** @type {*} */ colIndex, /** @type {*} */ value, verticalAlignment = "bottom") {
  const cell = worksheet.getCell(rowIndex, colIndex);
  cell.value = value;
  cell.alignment = { wrapText: true, vertical: verticalAlignment };
  worksheet.unMergeCells(rowIndex, 1, rowIndex, DOC_END_COL);
  worksheet.mergeCells(rowIndex, 1, rowIndex, DOC_END_COL);
}

const DISCLAIMER_TEXT_INDENT = 8;

function _fillDisclaimerMergeAndHeight(/** @type {*} */ worksheet, /** @type {*} */ rowIndex, /** @type {*} */ colIndex, /** @type {*} */ value) {
  try {
    worksheet.unMergeCells(rowIndex, 1, rowIndex, DOC_END_COL);
  } catch (_) {
    // Row may already be unmerged.
  }

  for (let col = 1; col <= DOC_END_COL; col++) {
    worksheet.getCell(rowIndex, col).value = null;
  }

  const cell = worksheet.getCell(rowIndex, colIndex);
  cell.value = value;
  cell.alignment = {
    wrapText: true,
    vertical: "top",
    horizontal: "left",
    indent: DISCLAIMER_TEXT_INDENT,
  };
  worksheet.mergeCells(rowIndex, 1, rowIndex, DOC_END_COL);
}

function _findDisclaimerImage(/** @type {*} */ worksheet, /** @type {*} */ nearRow) {
  const images = worksheet.getImages().filter((/** @type {*} */ image) => image.range?.tl && (image.range.ext || image.range.br));
  if (!images.length) return null;

  // Prefer the image anchored nearest the disclaimer message row (not the top logo).
  if (nearRow != null) {
    const targetNativeRow = nearRow - 1;
    return images.reduce((/** @type {*} */ best, /** @type {*} */ image) => {
      if (!best) return image;
      const bestDistance = Math.abs(best.range.tl.nativeRow - targetNativeRow);
      const nextDistance = Math.abs(image.range.tl.nativeRow - targetNativeRow);
      return nextDistance < bestDistance ? image : best;
    }, null);
  }

  // Fallback: original behavior — leftmost image that uses a two-cell anchor.
  return images.find((/** @type {*} */ image) => image.range?.br && image.range.tl.nativeCol === 0) || null;
}

function _computeImageExtentFromAnchors(/** @type {*} */ worksheet, /** @type {*} */ tl, /** @type {*} */ br) {
  let widthEMU = br.nativeColOff - tl.nativeColOff;
  let heightEMU = br.nativeRowOff - tl.nativeRowOff;

  for (let col = tl.nativeCol; col < br.nativeCol; col++) {
    const column = worksheet.getColumn(col + 1);
    const colWidth = column?.width ? Math.floor(column.width * 10000) : 640000;
    widthEMU += colWidth;
  }

  for (let row = tl.nativeRow; row < br.nativeRow; row++) {
    const rowHeight = worksheet.getRow(row + 1).height || 15;
    heightEMU += Math.floor(rowHeight * EMU_PER_POINT);
  }

  return {
    width: Math.max(1, Math.round(widthEMU / EMU_PER_PIXEL_AT_96_DPI)),
    height: Math.max(1, Math.round(heightEMU / EMU_PER_PIXEL_AT_96_DPI)),
  };
}

function _captureDisclaimerImageLayout(/** @type {*} */ worksheet, /** @type {*} */ nearRow) {
  const image = _findDisclaimerImage(worksheet, nearRow);
  if (!image?.range?.tl) return null;

  const { tl, br, ext } = image.range;
  const resolvedExt = ext ? { ...ext } : br ? _computeImageExtentFromAnchors(worksheet, tl, br) : null;

  if (!resolvedExt) return null;

  return {
    imageId: image.imageId,
    nativeCol: tl.nativeCol,
    topRowOffset: tl.nativeRowOff,
    leftColOffset: tl.nativeColOff,
    ext: resolvedExt,
  };
}

function _alignDisclaimerIcon(/** @type {*} */ worksheet, /** @type {*} */ startRow, /** @type {*} */ layout) {
  if (!layout?.ext || layout.imageId == null) return;

  const image = worksheet.getImages().find((/** @type {*} */ img) => img.imageId === layout.imageId);
  if (!image) return;

  const { tl } = image.range;
  tl.nativeRow = startRow - 1;
  tl.nativeRowOff = layout.topRowOffset;
  tl.nativeCol = layout.nativeCol ?? 0;
  tl.nativeColOff = layout.leftColOffset;

  // Keep a fixed size (oneCell) so row growth does not stretch the icon,
  // while still anchoring it to the disclaimer cell coordinates.
  image.range.br = undefined;
  image.range.ext = { ...layout.ext };
  image.range.editAs = "oneCell";
}

const PRICE_NUMBER_FORMAT = "#,##0";
const ACCOMMODATION_CITY_START_COL = 1;
const ACCOMMODATION_CITY_END_COL = 3;
const ACCOMMODATION_ARROW_COL = 4;
const ACCOMMODATION_HOTEL_START_COL = 5;
const ACCOMMODATION_HOTEL_END_COL = 7;

function _cloneAccommodationAppearance(/** @type {*} */ templateAppearance) {
  const appearance = JSON.parse(JSON.stringify(templateAppearance || { style: {}, alignment: {} }));

  if (appearance.style) {
    delete appearance.style.numFmt;
  }

  appearance.alignment = {
    ...(appearance.style?.alignment || {}),
    ...(appearance.alignment || {}),
  };

  return appearance;
}

function _applyAccommodationCellAppearance(/** @type {*} */ cell, /** @type {*} */ templateAppearance) {
  _applyCellAppearance(cell, _cloneAccommodationAppearance(templateAppearance));
}

function _applyPriceNumberFormat(/** @type {*} */ cell) {
  cell.numFmt = PRICE_NUMBER_FORMAT;
}

function _replaceCurrencyInFullWidthHeader(/** @type {*} */ worksheet, /** @type {*} */ headerCell, /** @type {*} */ currencyLabel, endCol = DOC_END_COL) {
  const row = headerCell.row;
  const title = String(headerCell.value).replace("[currency]", currencyLabel);

  _tryUnmergeRowInColRange(worksheet, row, 1, Math.max(endCol, DOC_END_COL));
  for (let col = 1; col <= Math.max(endCol, DOC_END_COL); col++) {
    worksheet.getCell(row, col).value = null;
  }

  worksheet.getCell(row, 1).value = title;
  worksheet.mergeCells(row, 1, row, endCol);
}

function _formatMergedTableHeader(/** @type {*} */ worksheet, /** @type {*} */ row, /** @type {*} */ startCol, /** @type {*} */ endCol, /** @type {*} */ title, /** @type {*} */ templateCell) {
  _tryUnmergeRowInColRange(worksheet, row, startCol, endCol);
  for (let col = startCol; col <= endCol; col++) {
    worksheet.getCell(row, col).value = null;
  }

  const cell = worksheet.getCell(row, startCol);
  cell.value = title;
  if (templateCell) {
    cell.style = { ...templateCell.style };
    cell.fill = templateCell.fill;
    cell.border = { ...templateCell.border };
    cell.alignment = templateCell.alignment ? { ...templateCell.alignment } : undefined;
  }
  worksheet.mergeCells(row, startCol, row, endCol);
}

function getAccommodationsLayout(/** @type {*} */ worksheet) {
  const bottomCityCell = _getCellByValue(worksheet, "[city]");
  const bottomHotelCell = _getCellByValue(worksheet, "[hotel]");
  const bottomHeaderCell = _getCellByValue(worksheet, "Hébergements (ou similaire)");

  if (!bottomCityCell || !bottomHotelCell || !bottomHeaderCell) {
    return null;
  }

  const contentRow = bottomCityCell.row;
  const templateCityCell = worksheet.getCell(contentRow, ACCOMMODATION_CITY_START_COL);
  const templateArrowCell = worksheet.getCell(contentRow, ACCOMMODATION_ARROW_COL);
  const templateHotelCell = worksheet.getCell(contentRow, ACCOMMODATION_HOTEL_START_COL);

  const headerTemplate = _captureCellAppearance(bottomHeaderCell);

  const templateCells = {
    cityRow: contentRow,
    cityAppearance: _captureCellAppearance(templateCityCell),
    arrowAppearance: _captureCellAppearance(templateArrowCell),
    arrowValue: templateArrowCell.value,
    hotelAppearance: _captureCellAppearance(templateHotelCell),
  };

  return {
    headerRow: bottomHeaderCell.row,
    headerEndRow: bottomHeaderCell.row,
    headerStartCol: ACCOMMODATION_CITY_START_COL,
    headerEndCol: DOC_END_COL,
    contentStartRow: bottomCityCell.row,
    cityStartCol: ACCOMMODATION_CITY_START_COL,
    cityMergeEndCol: ACCOMMODATION_CITY_END_COL,
    arrowCol: ACCOMMODATION_ARROW_COL,
    hotelCol: ACCOMMODATION_HOTEL_START_COL,
    hotelMergeEndCol: ACCOMMODATION_HOTEL_END_COL,
    headerTemplate,
    templateCells,
  };
}

function _tryUnmergeRowInColRange(/** @type {*} */ worksheet, /** @type {*} */ row, /** @type {*} */ startCol, /** @type {*} */ endCol) {
  const merges = [...(worksheet.model.merges || [])];
  merges.forEach((mergeRange) => {
    const [startRef, endRef] = mergeRange.split(":");
    const startCell = worksheet.getCell(startRef);
    const endCell = worksheet.getCell(endRef);
    const rowOverlap = startCell.row <= row && endCell.row >= row;
    const colOverlap = startCell.col <= endCol && endCell.col >= startCol;
    if (rowOverlap && colOverlap) {
      try {
        worksheet.unMergeCells(mergeRange);
      } catch (_) {
        // Ignore cells that were already unmerged.
      }
    }
  });
}

function _clearColRange(/** @type {*} */ worksheet, /** @type {*} */ row, /** @type {*} */ startCol, /** @type {*} */ endCol) {
  for (let col = startCol; col <= endCol; col++) {
    worksheet.getCell(row, col).value = null;
  }
}

function _formatAccommodationMergedCell(/** @type {*} */ worksheet, /** @type {*} */ row, /** @type {*} */ startCol, /** @type {*} */ endCol, /** @type {*} */ templateAppearance, /** @type {*} */ value) {
  worksheet.mergeCellsWithoutStyle(row, startCol, row, endCol);
  const cell = worksheet.getCell(row, startCol);
  cell.value = value ?? "";
  _applyAccommodationCellAppearance(cell, templateAppearance);
}

function _formatAccommodationArrowCell(/** @type {*} */ worksheet, /** @type {*} */ row, /** @type {*} */ templateAppearance, /** @type {*} */ value) {
  const cell = worksheet.getCell(row, ACCOMMODATION_ARROW_COL);
  cell.value = value ?? "►";
  _applyAccommodationCellAppearance(cell, templateAppearance);
}
function _formatAccommodationsHeader(/** @type {*} */ worksheet, /** @type {*} */ startCol, /** @type {*} */ endCol, /** @type {*} */ startRow, endRow = startRow, /** @type {*} */ headerTemplate) {
  if (!headerTemplate) return;

  const title = "Hébergements (ou similaire)";

  for (let row = startRow; row <= endRow; row++) {
    _tryUnmergeRowInColRange(worksheet, row, startCol, endCol);
    _clearColRange(worksheet, row, startCol, endCol);
  }

  worksheet.mergeCellsWithoutStyle(startRow, startCol, endRow, endCol);
  const mergedCell = worksheet.getCell(startRow, startCol);
  mergedCell.value = title;
  _applyAccommodationCellAppearance(mergedCell, headerTemplate);
}

function _formatAccommodationsContent(/** @type {*} */ worksheet, /** @type {*} */ accommodations, /** @type {*} */ layout, /** @type {*} */ templateCells, /** @type {*} */ startRow, /** @type {*} */ rowCount) {
  for (let i = 0; i < rowCount; i++) {
    const row = startRow + i;
    const accommodation = accommodations[i];

    _tryUnmergeRowInColRange(worksheet, row, ACCOMMODATION_CITY_START_COL, ACCOMMODATION_HOTEL_END_COL);
    _clearColRange(worksheet, row, ACCOMMODATION_CITY_START_COL, ACCOMMODATION_HOTEL_END_COL);

    _formatAccommodationMergedCell(
      worksheet,
      row,
      ACCOMMODATION_CITY_START_COL,
      ACCOMMODATION_CITY_END_COL,
      templateCells.cityAppearance,
      accommodation?.city ?? ""
    );
    _formatAccommodationArrowCell(worksheet, row, templateCells.arrowAppearance, templateCells.arrowValue ?? "►");
    _formatAccommodationMergedCell(
      worksheet,
      row,
      ACCOMMODATION_HOTEL_START_COL,
      ACCOMMODATION_HOTEL_END_COL,
      templateCells.hotelAppearance,
      accommodation?.name ?? ""
    );
  }
}

async function exportOverviewToExcel(/** @type {*} */ env, /** @type {*} */ supplierProductData, /** @type {*} */ data, /** @type {*} */ marginPercentage) {
  // Setup workbook
  const response = await fetch("/templates/quote-template.xlsx");
  const arrayBuffer = await response.arrayBuffer();
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(arrayBuffer);
  const worksheet = workbook.getWorksheet("Template");
  if (!worksheet) {
    throw new Error('Worksheet "Template" was not found in the quote template.');
  }
  worksheet.name = "Quote Document";
  const disclaimerMessageCellForLayout = _getCellByValue(worksheet, "[disclaimer-message]");
  const disclaimerImageLayout = _captureDisclaimerImageLayout(worksheet, disclaimerMessageCellForLayout?.row);

  // Intialize data
  const overviewIncludedItems = data.quote.overview_included ? data.quote.overview_included.split("\n") : [];
  const overviewExcludedItems = data.quote.overview_excluded ? data.quote.overview_excluded.split("\n") : [];
  const overviewDisclaimerItems = data.quote.overview_disclaimer ? data.quote.overview_disclaimer.split("\n") : [];
  const peopleRanges = parsePeopleRanges(data.quote.people_ranges);
  /** @type {{ city?: string, name?: string }[]} */
  const accommodations = [];

  const costs = [...data.costs, ...data.logistics_costs];

  for (const cost of costs) {
    /** @type {string | undefined} */
    let supplierCity;
    /** @type {string | undefined} */
    let supplierName;
    let isRoom = false;
    if (cost.supplier_id != null && cost.product_id != null) {
      const product = supplierProductData[cost.supplier_id].products[cost.product_id];
      if (product.product_type == "ROOM") {
        const hotelClass = supplierProductData[cost.supplier_id].supplier.meta?.hotel_class
          ? `${"*".repeat(supplierProductData[cost.supplier_id].supplier.meta.hotel_class)}`
          : null;
        supplierCity = `${supplierProductData[cost.supplier_id].supplier?.city}`;
        supplierName = `${supplierProductData[cost.supplier_id].supplier?.name}${hotelClass ? ` ${hotelClass}` : ""}`;
        isRoom = true;
      }
    } else if (cost.product_id == null && cost.product_type == "ROOM") {
      if (cost.supplier_id != null) {
        const hotelClass = supplierProductData[cost.supplier_id].supplier.meta?.hotel_class
          ? `${"*".repeat(supplierProductData[cost.supplier_id].supplier.meta.hotel_class)}`
          : null;
        supplierCity = `${supplierProductData[cost.supplier_id].supplier?.city}`;
        supplierName = `${supplierProductData[cost.supplier_id].supplier?.name}${hotelClass ? ` ${hotelClass}` : ""}`;
      } else {
        supplierCity = cost.supplier_city;
        supplierName = cost.supplier_name;
      }
      isRoom = true;
    }
    if (isRoom) {
      const isAccommodationExisting = accommodations.some(
        (item) => item.city === supplierCity && item.name === supplierName
      );
      if (!isAccommodationExisting) {
        accommodations.push({ city: supplierCity, name: supplierName });
      }
    }
  }

  const peopleRangesLength = peopleRanges.length;
  const accommodationsLength = accommodations.length;
  const totalCostPax = data.quote.total_cost_pax != null ? data.quote.total_cost_pax : [];
  const perPaxs = getPerPaxsByTotalCostPax(totalCostPax, data.quote, peopleRangesLength);
  const comparisonPeriods = parseComparisonPeriods(data.quote.comparison_periods);
  const comparisonPeriodPerPaxs = comparisonPeriods.map((comparisonPeriod) =>
    getComparisonPeriodPerPaxs(env, supplierProductData, data, comparisonPeriod, peopleRangesLength)
  );
  const comparisonPeriodSupplements = comparisonPeriods.map((comparisonPeriod) =>
    getComparisonPeriodSupplementValues(env, supplierProductData, data, comparisonPeriod)
  );
  const primaryPeriod = mapApiDateToPeriod(data.quote.start_date, data.quote.end_date);
  const allPeriods = [primaryPeriod, ...comparisonPeriods.map(getComparisonPeriodValue)];
  const allPeriodDisplayNames = getAllPeriodDisplayNames(
    data.quote.period_name ?? data.quote.periodName ?? data.quote.program?.period_name,
    primaryPeriod,
    comparisonPeriods
  );

  const currentDate = new Date().toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const startDay = data.quote.start_date.day != null ? data.quote.start_date.day : 1;
  const startMonth = data.quote.start_date.month != null ? data.quote.start_date.month - 1 : 0;
  const date = new Date(data.quote.start_date.year, startMonth, startDay);
  const startDate = date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  const supplements = getStoredRoomSupplementValues(
    data.costs,
    data.logistics_costs,
    marginPercentage,
    data.quote.discount_margin,
    getSupplementOverridesFromQuote(data.quote)
  );

  // Setup layout
  const includedMessageCell = _getCellByValue(worksheet, "[included-message]");
  _duplicateRowsAndDetachStyles(worksheet, includedMessageCell.row, overviewIncludedItems.length - 1);

  const excludedMessageCell = _getCellByValue(worksheet, "[excluded-message]");
  _duplicateRowsAndDetachStyles(worksheet, excludedMessageCell.row, overviewExcludedItems.length - 1);

  const disclaimerMessageCell = _getCellByValue(worksheet, "[disclaimer-message]");
  _duplicateRowsAndDetachStyles(worksheet, disclaimerMessageCell.row, overviewDisclaimerItems.length - 1);

  const peopleRangeCell = _getCellByValue(worksheet, "[people-range]");
  const perPaxCell = _getCellByValue(worksheet, "[per-pax]");
  _duplicateRowsAndDetachStyles(worksheet, peopleRangeCell.row, peopleRangesLength - 1);

  const accommodationCityCell = _getCellByValue(worksheet, "[city]");
  if (accommodationsLength > 1) {
    _duplicateRowsAndDetachStyles(worksheet, accommodationCityCell.row, accommodationsLength - 1);
  }

  const getComparisonPerPaxCol = (/** @type {*} */ index) => perPaxCell.col + (index + 1) * 2;

  comparisonPeriodPerPaxs.forEach((_, index) => {
    const comparisonPerPaxCol = getComparisonPerPaxCol(index);
    const headerCell = worksheet.getCell(perPaxCell.row - 1, comparisonPerPaxCol);
    const templateHeaderCell = worksheet.getCell(perPaxCell.row - 1, perPaxCell.col);

    headerCell.value = allPeriodDisplayNames[index + 1];
    headerCell.style = { ...templateHeaderCell.style };
    headerCell.border = { ...templateHeaderCell.border };
    headerCell.fill = templateHeaderCell.fill;
    headerCell.alignment = templateHeaderCell.alignment;

    const placeholderCell = worksheet.getCell(perPaxCell.row, comparisonPerPaxCol);
    placeholderCell.value = "";
    placeholderCell.style = { ...perPaxCell.style };
    placeholderCell.border = { ...perPaxCell.border };
    placeholderCell.fill = perPaxCell.fill;
    placeholderCell.alignment = perPaxCell.alignment;
    _applyPriceNumberFormat(placeholderCell);
    worksheet.getColumn(comparisonPerPaxCol).width = worksheet.getColumn(perPaxCell.col).width;
  });

  // Merged cells
  const cotationCell = _getCellByValue(worksheet, "Prix en [currency] par personne en occupation double");
  const currencyLabel = env === "USA" ? "USD" : "CAD";
  const cotationEndCol =
    comparisonPeriodPerPaxs.length > 0 ? getComparisonPerPaxCol(comparisonPeriodPerPaxs.length - 1) : 3;

  const supplementHeaderCell = _getCellByValue(worksheet, "Suppléments / Réductions");
  const singleCell = _getCellByValue(worksheet, "[single]");

  _replaceCurrencyInFullWidthHeader(worksheet, cotationCell, currencyLabel, cotationEndCol);

  if (supplementHeaderCell) {
    _formatMergedTableHeader(
      worksheet,
      supplementHeaderCell.row,
      1,
      cotationEndCol,
      "Suppléments / Réductions",
      supplementHeaderCell
    );
  }

  _applyPriceNumberFormat(perPaxCell);

  // Replace values
  _fillHeaderField(worksheet, "[client-name]", data.client.name);
  const programTitleCell = _fillHeaderField(worksheet, "[title]", data.quote.title, { wrapText: true });
  _fillHeaderField(worksheet, "[duration]", formatQuoteDuration(data.quote.number_of_nights));
  _getCellByValue(worksheet, "[reference]").value = data.quote.reference;
  _getCellByValue(worksheet, "[current-date]").value = currentDate;
  _normalizeQuoteHeaderRowsHeight(worksheet);
  const periodCell = _getCellByValue(worksheet, "[period]");
  const twinCell = _getCellByValue(worksheet, "[twin]");
  const tripleCell = _getCellByValue(worksheet, "[triple]");
  const quadCell = _getCellByValue(worksheet, "[quad]");
  const childCell = _getCellByValue(worksheet, "[child]");

  const arrivalText = allPeriods.length > 1 ? "Voir ci-dessous" : startDate;
  _fillArrivalField(worksheet, arrivalText);
  periodCell.value = allPeriodDisplayNames[0];
  singleCell.value = roundUpTo5(supplements.single);
  twinCell.value = roundUpTo5(supplements.twin);
  tripleCell.value = supplements.tripleIsOverride ? supplements.triple : roundDownTo5(supplements.triple);
  quadCell.value = supplements.quadIsOverride ? supplements.quad : roundDownTo5(supplements.quad);
  childCell.value = roundDownTo5(supplements.child);
  [singleCell, twinCell, tripleCell, quadCell, childCell].forEach(_applyPriceNumberFormat);

  writeComparisonPeriodValues(
    worksheet,
    singleCell,
    comparisonPeriodSupplements.map((supplement) => supplement.single),
    roundUpTo5
  );
  writeComparisonPeriodValues(
    worksheet,
    twinCell,
    comparisonPeriodSupplements.map((supplement) => supplement.twin),
    roundUpTo5
  );
  writeComparisonPeriodValues(
    worksheet,
    tripleCell,
    comparisonPeriodSupplements.map((supplement) =>
      supplement.tripleIsOverride ? supplement.triple : roundDownTo5(supplement.triple)
    ),
    (value) => value
  );
  writeComparisonPeriodValues(
    worksheet,
    quadCell,
    comparisonPeriodSupplements.map((supplement) =>
      supplement.quadIsOverride ? supplement.quad : roundDownTo5(supplement.quad)
    ),
    (value) => value
  );
  writeComparisonPeriodValues(
    worksheet,
    childCell,
    comparisonPeriodSupplements.map((supplement) => supplement.child),
    roundDownTo5
  );
  applySupplementTableBorders(
    worksheet,
    supplementHeaderCell ? supplementHeaderCell.row : singleCell.row,
    childCell.row,
    cotationEndCol,
    [singleCell.col, ...comparisonPeriodSupplements.map((_, index) => singleCell.col + (index + 1) * 2)],
    { hasHeaderRow: !!supplementHeaderCell }
  );

  const includedMessageCell2 = _getCellByValue(worksheet, "[included-message]");
  for (let i = 0; i < overviewIncludedItems.length; i++) {
    _fillMergeAndHeight(worksheet, includedMessageCell2.row + i, includedMessageCell2.col, overviewIncludedItems[i]);
  }

  const excludedMessageCell2 = _getCellByValue(worksheet, "[excluded-message]");
  for (let i = 0; i < overviewExcludedItems.length; i++) {
    _fillMergeAndHeight(
      worksheet,
      excludedMessageCell2.row + i,
      excludedMessageCell2.col,
      overviewExcludedItems[i],
      i === 0 ? "bottom" : "top"
    );
  }

  const disclaimerMessageCell2 = _getCellByValue(worksheet, "[disclaimer-message]");
  const disclaimerRowsToFill = Math.max(overviewDisclaimerItems.length, 1);
  for (let i = 0; i < disclaimerRowsToFill; i++) {
    _fillDisclaimerMergeAndHeight(
      worksheet,
      disclaimerMessageCell2.row + i,
      disclaimerMessageCell2.col,
      overviewDisclaimerItems[i] ?? ""
    );
  }
  _alignDisclaimerIcon(worksheet, disclaimerMessageCell2.row, disclaimerImageLayout);

  const overviewTextRows = {
    includedStart: includedMessageCell2.row,
    includedCount: overviewIncludedItems.length,
    includedCol: includedMessageCell2.col,
    excludedStart: excludedMessageCell2.row,
    excludedCount: overviewExcludedItems.length,
    excludedCol: excludedMessageCell2.col,
    disclaimerStart: disclaimerMessageCell2.row,
    disclaimerCount: disclaimerRowsToFill,
    disclaimerCol: disclaimerMessageCell2.col,
  };

  const peopleRangeRowNumber = peopleRangeCell.row;
  for (let i = 0; i < peopleRangesLength; i++) {
    const rowNumber = peopleRangeRowNumber + i;

    if (peopleRanges[i] != null) {
      worksheet.getCell(rowNumber, peopleRangeCell.col).value = peopleRanges[i].min + " - " + peopleRanges[i].max;
      worksheet.getCell(rowNumber, perPaxCell.col).value = Number(perPaxs[i]);
      _applyPriceNumberFormat(worksheet.getCell(rowNumber, perPaxCell.col));
      comparisonPeriodPerPaxs.forEach((comparisonPerPaxs, index) => {
        const comparisonPerPaxCell = worksheet.getCell(rowNumber, getComparisonPerPaxCol(index));
        comparisonPerPaxCell.value = getExcelNumberOrBlank(comparisonPerPaxs[i]);
        comparisonPerPaxCell.style = { ...worksheet.getCell(rowNumber, perPaxCell.col).style };
        comparisonPerPaxCell.border = { ...worksheet.getCell(rowNumber, perPaxCell.col).border };
        comparisonPerPaxCell.fill = worksheet.getCell(rowNumber, perPaxCell.col).fill;
        comparisonPerPaxCell.alignment = worksheet.getCell(rowNumber, perPaxCell.col).alignment;
        _applyPriceNumberFormat(comparisonPerPaxCell);
      });

      if (i < peopleRanges.length - 1) {
        worksheet.getCell(rowNumber, perPaxCell.col).border.bottom = undefined;
        comparisonPeriodPerPaxs.forEach((_, index) => {
          worksheet.getCell(rowNumber, getComparisonPerPaxCol(index)).border.bottom = undefined;
        });
      }
    } else {
      worksheet.getCell(rowNumber, peopleRangeCell.col).value = "";
      worksheet.getCell(rowNumber, perPaxCell.col).value = "";
      worksheet.getCell(rowNumber, perPaxCell.col).border = {};
      comparisonPeriodPerPaxs.forEach((_, index) => {
        const comparisonPerPaxCell = worksheet.getCell(rowNumber, getComparisonPerPaxCol(index));
        comparisonPerPaxCell.value = "";
        comparisonPerPaxCell.border = {};
      });
    }
  }

  const accommodationRowsToFill = Math.max(accommodationsLength, 1);
  const accommodationsLayout = getAccommodationsLayout(worksheet);

  if (accommodationsLayout) {
    _formatAccommodationsHeader(
      worksheet,
      accommodationsLayout.headerStartCol,
      accommodationsLayout.headerEndCol,
      accommodationsLayout.headerRow,
      accommodationsLayout.headerEndRow,
      accommodationsLayout.headerTemplate
    );
    _formatAccommodationsContent(
      worksheet,
      accommodations,
      accommodationsLayout,
      accommodationsLayout.templateCells,
      accommodationsLayout.contentStartRow,
      accommodationRowsToFill
    );
  }

  // Period columns are resized above. Recalculate text-row height from those widths.
  _applyProgramTitleRowHeight(worksheet, programTitleCell);
  for (let i = 0; i < overviewTextRows.includedCount; i++) {
    _applyWrappedRowHeight(worksheet, overviewTextRows.includedStart + i, overviewTextRows.includedCol);
  }
  for (let i = 0; i < overviewTextRows.excludedCount; i++) {
    _applyWrappedRowHeight(worksheet, overviewTextRows.excludedStart + i, overviewTextRows.excludedCol);
  }
  for (let i = 0; i < overviewTextRows.disclaimerCount; i++) {
    _applyWrappedRowHeight(
      worksheet,
      overviewTextRows.disclaimerStart + i,
      overviewTextRows.disclaimerCol,
      DISCLAIMER_TEXT_INDENT
    );
  }

  // Download updated file
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const link = document.createElement("a");
  const fileName = `SPN - Prix ${data.quote.number_of_nights} nuits - ${data.quote.title} - ${data.quote.reference}`;
  link.href = URL.createObjectURL(blob);
  link.download = fileName + ".xlsx";
  link.click();
}

function exportDetailToExcel(/** @type {*} */ env, /** @type {*} */ supplierProductData, /** @type {*} */ data, /** @type {*} */ pax, /** @type {*} */ periodKey = "primary") {
  const comparisonPeriod = resolveItemizedExportComparisonPeriod(data, periodKey);
  data = buildItemizedExportData(env, supplierProductData, data, comparisonPeriod);

  const styles = {
    font: {
      avenirFont10BlackBold: { font: { name: "Avenir", size: 10, color: { argb: "FF000000" }, bold: true } },
      avenirBookFont10Black: { font: { name: "Avenir Book", size: 10, color: { argb: "FF000000" } } },
      avenirBookFont10BlackBold: { font: { name: "Avenir Book", size: 10, color: { argb: "FF000000" }, bold: true } },
      avenirBookFont10BlackItalic: {
        font: { name: "Avenir Book", size: 10, color: { argb: "FF000000" }, italic: true },
      },
      avenirBookFont11BlackBold: { font: { name: "Avenir Book", size: 11, color: { argb: "FF000000" }, bold: true } },
      avenirBookFont11BlackItalic: {
        font: { name: "Avenir Book", size: 11, color: { argb: "FF000000" }, italic: true },
      },
      avenirBookFont12Black: { font: { name: "Avenir Book", size: 12, color: { argb: "FF000000" } } },
      avenirBookFont12BlackBold: { font: { name: "Avenir Book", size: 12, color: { argb: "FF000000" }, bold: true } },
      avenirBookFont16Black: { font: { name: "Avenir Book", size: 16, color: { argb: "FF000000" } } },
      avenirBookFont14WhiteBold: { font: { name: "Avenir Book", size: 14, color: { argb: "FFFFFFFF" }, bold: true } },
      avenirBookFont16WhiteBold: { font: { name: "Avenir Book", size: 16, color: { argb: "FFFFFFFF" }, bold: true } },
    },
    alignment: {
      left: { alignment: { vertical: "middle", horizontal: "left" } },
      center: { alignment: { vertical: "middle", horizontal: "center" } },
      right: { alignment: { vertical: "middle", horizontal: "right" } },
    },
    fill: {
      goldBackground: { fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFC9A362" } } },
      blue1Background: { fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF8DB4DB" } } },
      blue2Background: { fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFDAE5F3" } } },
      grey1Background: { fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFC6C6C6" } } },
      orangeBackground: { fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFBC00" } } },
    },
    border: {
      all: {
        border: {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
        },
      },
      topLeftRight: {
        border: {
          top: { style: "thin", color: { argb: "FF000000" } },
          left: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        },
      },
      left: {
        border: {
          left: { style: "thin", color: { argb: "FF000000" } },
        },
      },
      right: {
        border: {
          right: { style: "thin", color: { argb: "FF000000" } },
        },
      },
      leftRight: {
        border: {
          left: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
        },
      },
      bottomLeft: {
        border: {
          left: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
        },
      },
      bottomRight: {
        border: {
          right: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
        },
      },
      bottomLeftRight: {
        border: {
          left: { style: "thin", color: { argb: "FF000000" } },
          right: { style: "thin", color: { argb: "FF000000" } },
          bottom: { style: "thin", color: { argb: "FF000000" } },
        },
      },
      bottom: {
        border: {
          bottom: { style: "thin", color: { argb: "FF000000" } },
        },
      },
    },
  };

  const currentDate = new Date().toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" });

  const rows = [
    ["", "", "", "Soumission faite le :", ""],
    ["", "", "", currentDate, ""],
    ["", "", "", "", ""],
    ["", "", "", data.quote.title, ""],
    ["", "", "", "", ""],
    ["", "", "COUTS VARIABLES PAR ADULTE - BASE DOUBLE", "", ""],
    ["", "", "Prestation", "Itinéraire", "Prix de"],
    ["", "", "", "", "vente"],
    ["", "Jour", "", "", ""],
  ];

  /** @type {Array<{ column?: number, row?: number, cell?: number, options: object }>} */
  const config = [
    { column: 1, options: { width: 9.74 } },
    { column: 2, options: { width: 12.07 } },
    { column: 3, options: { width: 36.81 } },
    { column: 4, options: { width: 42 } },
    { column: 5, options: { width: 16 } },
    { row: 1, cell: 4, options: { ...styles.font.avenirBookFont10Black, ...styles.alignment.right } },
    { row: 2, cell: 4, options: { ...styles.font.avenirBookFont10Black, ...styles.alignment.right } },
    { row: 4, cell: 4, options: { ...styles.font.avenirBookFont16Black, ...styles.alignment.right } },
    {
      row: 6,
      cell: 3,
      options: { ...styles.font.avenirBookFont16WhiteBold, ...styles.alignment.center, ...styles.fill.goldBackground },
    },
    {
      row: 7,
      cell: 3,
      options: { ...styles.font.avenirBookFont14WhiteBold, ...styles.alignment.center, ...styles.fill.blue1Background },
    },
    {
      row: 7,
      cell: 4,
      options: { ...styles.font.avenirBookFont14WhiteBold, ...styles.alignment.center, ...styles.fill.blue1Background },
    },
    {
      row: 7,
      cell: 5,
      options: { ...styles.font.avenirBookFont14WhiteBold, ...styles.alignment.center, ...styles.fill.blue1Background },
    },
    {
      row: 8,
      cell: 4,
      options: { ...styles.font.avenirBookFont14WhiteBold, ...styles.alignment.center, ...styles.fill.blue1Background },
    },
    {
      row: 8,
      cell: 5,
      options: { ...styles.font.avenirBookFont14WhiteBold, ...styles.alignment.center, ...styles.fill.blue1Background },
    },
    { row: 9, cell: 2, options: { ...styles.font.avenirFont10BlackBold, ...styles.alignment.center } },
  ];

  const mergeCells = ["D1:E1", "D2:E2", "D4:E4", "C6:E6", "C7:C8", "D7:D8"];

  let totalVariableCosts = 0;
  let totalFixedCosts = 0;
  /** @type {number | null} */
  let previousDay = null;
  let startDate = null;

  const { day, month, year } = data?.quote?.start_date ?? {};
  if (day && month && year) {
    startDate = new Date(year, month - 1, day);
  }

  const sortedVariableCosts = data.costs
    .filter((/** @type {*} */ cost) => cost.cost_type === "PP")
    .sort((/** @type {*} */ a, /** @type {*} */ b) => a.day - b.day || a.order_in_day - b.order_in_day);

  const programDays = data.quote.program?.days ?? data.quote.city_routes ?? [];
  const logisticsUUIDs = (data.logistics_costs || []).map((/** @type {*} */ cost) => cost.uuid);
  const numberOfClients = Number(pax) || 0;
  const peopleRanges = parsePeopleRanges(data.quote.people_ranges);
  const highestRangeMarginPercent = getMarginPercentageFromFields(data.quote, peopleRanges);
  const marginPercent = getMarginPercentForPax(data.quote, numberOfClients, highestRangeMarginPercent);
  const applySaleMargin = (/** @type {*} */ amount) =>
    roundToTwoDecimals((Number(amount) || 0) * (1 + marginPercent / 100));

  sortedVariableCosts.forEach((/** @type {*} */ cost) => {
    if (previousDay !== cost.day) {
      if (previousDay !== null) {
        rows.push(["", "", "", "", ""]);
        config.push(
          { row: rows.length, cell: 3, options: { ...styles.border.leftRight } },
          { row: rows.length, cell: 4, options: { ...styles.border.leftRight } },
          {
            row: rows.length,
            cell: 5,
            options: { ...styles.border.leftRight, ...styles.fill.blue2Background },
          }
        );
      }

      let dateString = "";
      if (startDate) {
        const adjustedDate = new Date(startDate);
        adjustedDate.setDate(startDate.getDate() + cost.day);
        dateString = adjustedDate.toLocaleDateString("fr-FR", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      }

      const programDay = programDays[cost.day] || {};
      const itinerary = formatRouteItinerary(
        findQuoteRoute(data.routes, programDay.route_id),
        programDay.start,
        programDay.end
      );

      rows.push(["", cost.day + 1, dateString, itinerary, ""]);
      config.push(
        {
          row: rows.length,
          cell: 2,
          options: { ...styles.font.avenirFont10BlackBold, ...styles.alignment.center },
        },
        {
          row: rows.length,
          cell: 3,
          options: {
            ...styles.font.avenirBookFont10BlackBold,
            ...styles.alignment.center,
            ...styles.fill.grey1Background,
            ...styles.border.all,
          },
        },
        {
          row: rows.length,
          cell: 4,
          options: {
            ...styles.font.avenirBookFont10Black,
            ...styles.alignment.center,
            ...styles.border.all,
          },
        },
        {
          row: rows.length,
          cell: 5,
          options: { ...styles.font.avenirBookFont10BlackBold, ...styles.alignment.center, ...styles.border.all },
        }
      );
      previousDay = cost.day;
    }

    const supplierName = cost.supplier_id ? supplierProductData[cost.supplier_id].supplier.name : cost.supplier_name;
    const productName = cost.product_id
      ? supplierProductData[cost.supplier_id].products[cost.product_id].name
      : cost.product_name;
    const productType =
      supplierProductData[cost.supplier_id]?.products?.[cost.product_id]?.product_type || cost.product_type;
    const unitPrice = parseStoredMoneyValue(cost.product_cost?.value?.default, productType) ?? 0;
    const foc = cost.supplier_id ? supplierProductData[cost.supplier_id]?.supplier?.meta?.foc : null;
    const perPerson = applySaleMargin(
      getVariableCostPerPerson(
        unitPrice,
        numberOfClients,
        countMatchedLogisticsSuppliers(cost.logistics_cost_uuids, logisticsUUIDs),
        productType,
        foc
      )
    );

    rows.push(["", "", `${supplierName} / ${productName}`, "", perPerson.toFixed(2)]);
    config.push(
      { row: rows.length, cell: 3, options: { ...styles.font.avenirBookFont10Black, ...styles.border.leftRight } },
      { row: rows.length, cell: 4, options: { ...styles.border.leftRight } },
      {
        row: rows.length,
        cell: 5,
        options: {
          ...styles.font.avenirBookFont10BlackItalic,
          ...styles.alignment.right,
          ...styles.border.leftRight,
          ...styles.fill.blue2Background,
        },
      }
    );

    totalVariableCosts += perPerson;
  });

  rows.push(["", "", "", "", ""]);
  config.push(
    { row: rows.length, cell: 3, options: { ...styles.border.bottomLeftRight } },
    { row: rows.length, cell: 4, options: { ...styles.border.bottomLeftRight } },
    {
      row: rows.length,
      cell: 5,
      options: { ...styles.border.bottomLeftRight, ...styles.fill.blue2Background },
    }
  );

  rows.push(["", "", "Frais variables par personne", "", totalVariableCosts.toFixed(2)]);
  config.push(
    {
      row: rows.length,
      cell: 3,
      options: { ...styles.font.avenirBookFont11BlackBold, ...styles.alignment.right },
    },
    { row: rows.length, cell: 5, options: { ...styles.font.avenirBookFont10Black, ...styles.alignment.right } }
  );

  rows.push(["", "", "", "", ""], ["", "", "", "", ""], ["", "", "COUTS FIXES PAR PERSONNE", "", ""]);
  config.push({
    row: rows.length,
    cell: 3,
    options: { ...styles.font.avenirBookFont16WhiteBold, ...styles.alignment.center, ...styles.fill.goldBackground },
  });

  mergeCells.push(`C${rows.length}:E${rows.length}`);

  rows.push(["", "", "Prestation", "Itinéraire", "Prix de"]);
  config.push(
    {
      row: rows.length,
      cell: 3,
      options: { ...styles.font.avenirBookFont14WhiteBold, ...styles.alignment.center, ...styles.fill.blue1Background },
    },
    {
      row: rows.length,
      cell: 4,
      options: { ...styles.font.avenirBookFont14WhiteBold, ...styles.alignment.center, ...styles.fill.blue1Background },
    },
    {
      row: rows.length,
      cell: 5,
      options: { ...styles.font.avenirBookFont14WhiteBold, ...styles.alignment.center, ...styles.fill.blue1Background },
    }
  );

  const benefitRowNo1 = rows.length;

  rows.push(["", "", "", "", "vente"]);
  config.push(
    {
      row: rows.length,
      cell: 4,
      options: { ...styles.font.avenirBookFont14WhiteBold, ...styles.alignment.center, ...styles.fill.blue1Background },
    },
    {
      row: rows.length,
      cell: 5,
      options: { ...styles.font.avenirBookFont14WhiteBold, ...styles.alignment.center, ...styles.fill.blue1Background },
    }
  );

  const benefitRowNo2 = rows.length;

  mergeCells.push(`C${benefitRowNo1}:C${benefitRowNo2}`, `D${benefitRowNo1}:D${benefitRowNo2}`);
  rows.push(["", "", "", "", ""]);

  /** @type {{ supplierName: string, productName: string, productCost: number }[]} */
  const fixedCostItems = [];

  data.logistics_costs.forEach((/** @type {*} */ cost) => {
    const supplierName = cost.supplier_id ? supplierProductData[cost.supplier_id].supplier.name : cost.supplier_name;
    const productName = cost.product_id
      ? supplierProductData[cost.supplier_id].products[cost.product_id].name
      : cost.product_name;
    const productCostValue = Number(cost.product_cost.value.default.substring(1));
    const days = cost.days != null ? JSON.parse(cost.days) : [];
    const productCost = applySaleMargin(productCostValue * days.length);

    fixedCostItems.push({ supplierName, productName, productCost });
    totalFixedCosts += productCost;
  });

  data.costs
    .filter((/** @type {*} */ cost) => ["PG", "PD"].includes(cost.cost_type))
    .forEach((/** @type {*} */ cost) => {
      const supplierName = cost.supplier_id ? supplierProductData[cost.supplier_id].supplier.name : cost.supplier_name;
      const productName = cost.product_id
        ? supplierProductData[cost.supplier_id].products[cost.product_id].name
        : cost.product_name;
      const productCost = applySaleMargin(Number(cost.product_cost.value.default.substring(1)));

      fixedCostItems.push({ supplierName, productName, productCost });
      totalFixedCosts += productCost;
    });

  fixedCostItems.forEach((fixedCostItem, index) => {
    const borderOptions = index === 0 ? styles.border.topLeftRight : styles.border.leftRight;
    rows.push([
      "",
      "",
      `${fixedCostItem.supplierName} / ${fixedCostItem.productName}`,
      "",
      fixedCostItem.productCost.toFixed(2),
    ]);
    config.push(
      { row: rows.length, cell: 3, options: { ...styles.font.avenirBookFont10Black, ...borderOptions } },
      { row: rows.length, cell: 4, options: { ...borderOptions } },
      {
        row: rows.length,
        cell: 5,
        options: {
          ...styles.font.avenirBookFont10BlackItalic,
          ...styles.fill.blue2Background,
          ...styles.alignment.right,
          ...borderOptions,
        },
      }
    );
  });

  rows.push(["", "", "", "", ""]);
  config.push(
    { row: rows.length, cell: 3, options: { ...styles.border.bottomLeftRight } },
    { row: rows.length, cell: 4, options: { ...styles.border.bottomLeftRight } },
    { row: rows.length, cell: 5, options: { ...styles.border.bottomLeftRight, ...styles.fill.blue2Background } }
  );

  rows.push(["", "", "Frais fixes pour tout le groupe", "", totalFixedCosts.toFixed(2)]);
  config.push(
    {
      row: rows.length,
      cell: 3,
      options: { ...styles.font.avenirBookFont11BlackBold, ...styles.alignment.right },
    },
    { row: rows.length, cell: 5, options: { ...styles.font.avenirBookFont10Black, ...styles.alignment.right } }
  );

  const currency = env == "USA" ? "USD" : "CAD";

  rows.push(["", "", "", "", ""], ["", "", `Prix en ${currency} par personne :`, "", ""]);
  config.push({
    row: rows.length,
    cell: 3,
    options: {
      ...styles.font.avenirBookFont12BlackBold,
      ...styles.alignment.center,
      ...styles.border.all,
      ...styles.fill.orangeBackground,
    },
  });

  mergeCells.push(`C${rows.length}:E${rows.length}`);

  rows.push(["", "", "", "", "Occup. double"]);
  config.push(
    {
      row: rows.length,
      cell: 3,
      options: { ...styles.border.left },
    },
    {
      row: rows.length,
      cell: 5,
      options: { ...styles.font.avenirBookFont12BlackBold, ...styles.alignment.center, ...styles.border.right },
    }
  );

  const costPerPerson = numberOfClients
    ? totalVariableCosts + totalFixedCosts / numberOfClients
    : totalVariableCosts;
  const sellingPricePerPerson = roundToCustom(costPerPerson);

  rows.push(["", "", `Base ${pax}`, "", sellingPricePerPerson]);

  const commonOptions = {
    ...styles.font.avenirBookFont12Black,
    ...styles.alignment.center,
  };

  const leftBorderOption = styles.border.bottomLeft;
  const rightBorderOption = styles.border.bottomRight;

  config.push(
    {
      row: rows.length,
      cell: 3,
      options: {
        ...commonOptions,
        ...leftBorderOption,
      },
    },
    {
      row: rows.length,
      cell: 4,
      options: {
        ...commonOptions,
        ...styles.border.bottom,
      },
    },
    {
      row: rows.length,
      cell: 5,
      options: {
        ...commonOptions,
        ...rightBorderOption,
        ...styles.font.avenirBookFont12BlackBold,
      },
    }
  );

  rows.push(["", "", "", "", ""], ["", "", `Supplement`, "", ""]);
  config.push({
    row: rows.length,
    cell: 3,
    options: {
      ...styles.font.avenirBookFont12BlackBold,
      ...styles.alignment.center,
      ...styles.border.all,
      ...styles.fill.orangeBackground,
    },
  });
  mergeCells.push(`C${rows.length}:E${rows.length}`);

  let totalCost = 0;
  let totalCostSingle = 0;
  let totalCostTwin = 0;
  let totalCostTriple = 0;
  let totalCostQuad = 0;

  [...data.costs, ...data.logistics_costs].forEach((cost) => {
    if (cost.product_type === "ROOM") {
      const productCost = Number(cost.product_cost.value.default.substring(1));
      totalCost += productCost;
      totalCostSingle += cost.product_cost.value.single
        ? Number(cost.product_cost.value.single.substring(1))
        : productCost;
      totalCostTwin += cost.product_cost.value.twin ? Number(cost.product_cost.value.twin.substring(1)) : productCost;
      totalCostTriple += cost.product_cost.value.triple
        ? Number(cost.product_cost.value.triple.substring(1))
        : productCost;
      totalCostQuad += cost.product_cost.value.quad ? Number(cost.product_cost.value.quad.substring(1)) : productCost;
    }
  });

  const discountMargin = data.quote.discount_margin;
  const supplementOverrides = getSupplementOverridesFromQuote(data.quote);
  const singleSupplement = totalCostSingle - totalCost / 2;
  const twinSupplement = (totalCostTwin / 2 - totalCost / 2) * (1 + highestRangeMarginPercent / 100);
  const tripleDiscount = getSupplementDisplayValue(
    (totalCostTriple / 3 - totalCost / 2) * (1 - discountMargin / 100),
    supplementOverrides.triple,
    roundDownTo5
  );
  const quadDiscount = getSupplementDisplayValue(
    (totalCostQuad / 4 - totalCost / 2) * (1 - discountMargin / 100),
    supplementOverrides.quad,
    roundDownTo5
  );
  const childRate = singleSupplement * (1 - highestRangeMarginPercent / 100) * -1;

  const highestMinPeopleRange = peopleRanges.length == 0 ? 0 : Math.max(...peopleRanges.map((range) => range.min));

  rows.push(["", "", "Single", "", roundUpTo5(singleSupplement)]);
  config.push(
    {
      row: rows.length,
      cell: 3,
      options: { ...commonOptions, ...styles.border.left },
    },
    {
      row: rows.length,
      cell: 5,
      options: { ...styles.font.avenirBookFont12BlackBold, ...styles.alignment.center, ...styles.border.right },
    }
  );

  rows.push(["", "", "Twin", "", roundUpTo5(twinSupplement)]);
  config.push(
    {
      row: rows.length,
      cell: 3,
      options: { ...commonOptions, ...styles.border.left },
    },
    {
      row: rows.length,
      cell: 5,
      options: { ...styles.font.avenirBookFont12BlackBold, ...styles.alignment.center, ...styles.border.right },
    }
  );

  rows.push(["", "", "Triple", "", tripleDiscount]);
  config.push(
    {
      row: rows.length,
      cell: 3,
      options: { ...commonOptions, ...styles.border.left },
    },
    {
      row: rows.length,
      cell: 5,
      options: { ...styles.font.avenirBookFont12BlackBold, ...styles.alignment.center, ...styles.border.right },
    }
  );

  rows.push(["", "", "Quad", "", quadDiscount]);
  config.push(
    {
      row: rows.length,
      cell: 3,
      options: { ...commonOptions, ...styles.border.left },
    },
    {
      row: rows.length,
      cell: 5,
      options: { ...styles.font.avenirBookFont12BlackBold, ...styles.alignment.center, ...styles.border.right },
    }
  );

  rows.push(["", "", "Child", "", roundDownTo5(childRate)]);
  config.push(
    {
      row: rows.length,
      cell: 3,
      options: { ...commonOptions, ...leftBorderOption },
    },
    {
      row: rows.length,
      cell: 4,
      options: { ...commonOptions, ...styles.border.bottom },
    },
    {
      row: rows.length,
      cell: 5,
      options: { ...styles.font.avenirBookFont12BlackBold, ...styles.alignment.center, ...rightBorderOption },
    }
  );

  const fileName = `SPN - Prix ${data.quote.number_of_nights} nuits - ${data.quote.title} - ${data.quote.reference}`;

  return ExportService({
    firstRowHeight: 15,
    iconArea: "A1:B4",
    fileName,
    sheetName: "Itemized Quote",
    rows: rows,
    config: config,
    mergeCell: mergeCells,
  });
}

function getSupplierTypeByProductType(/** @type {string} */ productType) {
  /** @type {Record<string, string>} */
  const supplierTypes = {
    ROOM: "HOTEL",
    MEAL: "RESTAURANT",
    EXCURSION: "EXCURSION",
    TOUR: "TOUR_GUIDE",
    TRANS: "TRANSPORT",
    EXPENSE: "PARK",
    TOUR_LEADER: "TOUR_GUIDE",
  };
  return supplierTypes[productType] || null;
}

function isInPeopleRangeByInput(/** @type {*} */ peopleRanges, /** @type {*} */ input) {
  if (input === null) return false;
  return peopleRanges.some((/** @type {*} */ range) => input >= range.min && input <= range.max);
}

function getLowestMinRange(/** @type {*} */ peopleRanges) {
  if (peopleRanges.length === 0) return 0;
  return Math.min(...peopleRanges.map((/** @type {*} */ range) => range.min));
}

function isTBDSupplier(/** @type {*} */ id) {
  const tbdSuppliers = [
    "tBfxF7rbQZYNBFL8dNcNE4", // USA, TBD
    "29TDACHNsKmwAwhro5VnMD", // CAD, TBA (to be advised)
  ];
  return tbdSuppliers.includes(id);
}

function isSupplierMatchingEnabled(/** @type {*} */ costs, /** @type {*} */ logisticsCosts) {
  const hasVariableMatch = costs.some((/** @type {*} */ vc) =>
    vc.items.some((/** @type {*} */ item) => ["ROOM", "MEAL", "EXCURSION"].includes(item.productType))
  );
  const hasFixedMatch = logisticsCosts.length > 0;
  return hasVariableMatch && hasFixedMatch;
}

function getCommissions(/** @type {*} */ costs, /** @type {*} */ min) {
  let totalCommission = 0;
  for (const cost of costs) {
    for (const item of cost.items) {
      if (item.productType === "ROOM") {
        const porterage = item.productCost.valueIncludesPorterage ? item.productCost.porterage : 0;
        const price = item.productCost.value.default - porterage;
        const rawCommission = item.supplier.commission?.value;
        const commissionValue = rawCommission === "no" || rawCommission == null ? 0 : Number(rawCommission);
        totalCommission += Number(price) * min * (commissionValue / 100);
      }
    }
  }
  return totalCommission;
}

function getSupplementValue(/** @type {*} */ costs, /** @type {*} */ peopleRanges, /** @type {*} */ margin, /** @type {*} */ discountMargin) {
  const index = peopleRanges.reduce((/** @type {*} */ maxIndex, /** @type {*} */ current, /** @type {*} */ index, /** @type {*} */ array) => {
    return current.min > array[maxIndex].min ? index : maxIndex;
  }, 0);
  const perPax = margin.perPax[index] ?? 0;
  const marginAmount = margin.status === "fixed" ? margin.percentFixed : (margin.percentDynamic[index] ?? 0);

  const totalCost = { default: 0, single: 0, twin: 0, triple: 0, quad: 0 };

  costs.forEach((/** @type {*} */ cost, /** @type {*} */ _) => {
    cost.items
      .filter((/** @type {*} */ item) => item.productType === "ROOM")
      .forEach((/** @type {*} */ item, /** @type {*} */ _) => {
        totalCost.default += item.productCost.value.default * 2;
        totalCost.single += item.productCost.value.single
          ? item.productCost.value.single * 2
          : item.productCost.value.default * 2;
        totalCost.twin += item.productCost.value.twin
          ? item.productCost.value.twin * 2
          : item.productCost.value.default * 2;
        totalCost.triple += item.productCost.value.triple
          ? item.productCost.value.triple * 2
          : item.productCost.value.default * 2;
        totalCost.quad += item.productCost.value.quad
          ? item.productCost.value.quad * 2
          : item.productCost.value.default * 2;
      });
  });

  const singleSupplement = totalCost.single - totalCost.default / 2;
  const twinSupplement = (totalCost.twin / 2 - totalCost.default / 2) * (1 + marginAmount / 100);
  const tripleDiscount = (totalCost.triple / 3 - totalCost.default / 2) * (1 - discountMargin / 100);
  const quadDiscount = (totalCost.quad / 4 - totalCost.default / 2) * (1 - discountMargin / 100);
  const childRate = singleSupplement * (1 - marginAmount / 100) * -1;

  return {
    single: singleSupplement,
    twin: twinSupplement,
    triple: tripleDiscount,
    quad: quadDiscount,
    child: childRate,
  };
}

function _isInDateRange(/** @type {*} */ item, /** @type {*} */ startMonth, /** @type {*} */ startDay, /** @type {*} */ endMonth, /** @type {*} */ endDay) {
  if (!(item.mstart && item.mend)) return true;

  // User-entered range
  let userStart = parseInt(`${startMonth}${_getDayString(startDay, true, startMonth)}`);
  let userEnd = parseInt(`${endMonth}${_getDayString(endDay, false, endMonth)}`);

  // Item range
  let itemStart = parseInt(`${item.mstart}${_getDayString(item.dstart, true, item.mstart)}`);
  let itemEnd = parseInt(`${item.mend}${_getDayString(item.dend, false, item.mend)}`);

  // Handle wrap-around dates (e.g., October to April)
  if (itemStart > itemEnd) {
    return userStart >= itemStart || userEnd >= itemStart || userStart <= itemEnd || userEnd <= itemEnd;
  }

  // Check overlapping ranges
  return (
    (userStart <= itemStart && itemStart <= userEnd) || // Item starts within user range
    (userStart <= itemEnd && itemEnd <= userEnd) || // Item ends within user range
    (itemStart <= userStart && userEnd <= itemEnd) // User range fully inside item range
  );
}

function _getDayString(/** @type {*} */ day, /** @type {*} */ isStart, /** @type {*} */ month) {
  if (!month) return "01";
  if (!day) return isStart ? "01" : MONTHS[month - 1].days; // Use first or last day of the month
  return day < 10 ? `0${day}` : `${day}`; // Ensure two-digit format
}

function _getDateByDayIndex(/** @type {*} */ year, /** @type {*} */ month, /** @type {*} */ day, /** @type {*} */ dayIndex) {
  const startDate = new Date(Number(year), Number(month) - 1, Number(day));
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + dayIndex);
  return endDate;
}

function roundToCustom(/** @type {*} */ value) {
  const sign = value < 0 ? -1 : 1;
  const absValue = Math.abs(value);
  const integerPart = Math.trunc(absValue);
  const lastDigit = integerPart % 10;
  let result;
  if (lastDigit <= 2) {
    result = integerPart - lastDigit;
  } else if (lastDigit <= 7) {
    result = integerPart - lastDigit + 5;
  } else {
    result = integerPart - lastDigit + 10;
  }
  return (result * sign).toString();
}

function roundUpTo5(/** @type {*} */ value) {
  return Math.ceil(value / 5) * 5;
}

function roundDownTo5(/** @type {*} */ value) {
  return Math.floor(value / 5) * 5;
}

export default {
  MAX_COMPARISON_PERIODS,
  generateUUID,
  getTotalCosts,
  getCostBreakdownByProductType,
  getHighestPeopleRangeIndex,
  exportOverviewToExcel,
  exportDetailToExcel,
  getSupplierTypeByProductType,
  isInPeopleRangeByInput,
  getLowestMinRange,
  isTBDSupplier,
  isSupplierMatchingEnabled,
  getFOCInfo,
  getCommissions,
  getSupplementValue,
  isSupplementOverrideSet,
  getSupplementDisplayValue,
  getProductCost,
  getProductProgramDescription,
  getProductProgramTitle,
  getProgramSupplierName,
  formatProgramWebsiteUrl,
  getProgramAccommodations,
  applyShowInProgramForProductType,
  getFrenchArrivalLabel,
  formatDayCities,
  buildQuoteFolderName,
  getQuoteOverviewPriceTable,
  roundToTwoDecimals,
  roundToCustom,
  roundUpTo5,
  roundDownTo5,
};
